import React, { useState } from 'react';
import { motion } from 'motion/react';
import { VdpElement, CSVData, Unit, BrandPalette } from '../../types';
import { macSpring } from '../../utils/macAnimations';
import { AlignLeft, AlignCenter, AlignRight, Underline, Strikethrough, Superscript, Subscript, ImageIcon, Download, FileCode, Copy, Check, Loader2, LayoutGrid, Maximize2, Lock, Unlock, Layers, Eye, EyeOff, FolderPlus, FolderMinus, ChevronDown, ChevronRight, SlidersHorizontal, Sparkles, Palette, Type, QrCode, Barcode as BarcodeIcon, Box, Calendar, DollarSign } from 'lucide-react';
import { formatUnit, parseUnit } from '../../utils/units';
import { formatDateValue } from '../../utils/shapeAndFormatUtils';
import { exportElementsAsPng, exportElementsAsSvg, copyElementsAsSvgCode, getElementsBoundingBox } from '../../utils/quickExportUtils';
import { GroupSelectionControls } from './GroupSelectionControls';
import { MaterialDetailSettingsPanel } from './MaterialDetailSettingsPanel';

interface AccordionSectionProps {
  id: string;
  title: string;
  icon?: React.ReactNode;
  badge?: React.ReactNode;
  isOpen: boolean;
  onToggle: () => void;
  children: React.ReactNode;
  className?: string;
}

const AccordionSection: React.FC<AccordionSectionProps> = ({
  title,
  icon,
  badge,
  isOpen,
  onToggle,
  children,
  className = ''
}) => {
  return (
    <div className={`rounded-lg overflow-hidden border border-zinc-800/80 bg-zinc-900/60 shadow-xs transition-colors ${className}`}>
      <button
        type="button"
        onClick={onToggle}
        className="w-full flex items-center justify-between px-3 py-2 bg-zinc-900/90 hover:bg-zinc-800/90 transition-colors text-left select-none cursor-pointer group"
      >
        <div className="flex items-center gap-2 min-w-0">
          {icon && <span className="text-zinc-400 group-hover:text-blue-400 transition-colors shrink-0">{icon}</span>}
          <span className="text-xs font-semibold text-zinc-200 group-hover:text-white truncate">{title}</span>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {badge}
          {isOpen ? (
            <ChevronDown className="w-3.5 h-3.5 text-zinc-400 group-hover:text-zinc-200 transition-transform duration-150" />
          ) : (
            <ChevronRight className="w-3.5 h-3.5 text-zinc-500 group-hover:text-zinc-300 transition-transform duration-150" />
          )}
        </div>
      </button>
      {isOpen && (
        <div className="p-3 border-t border-zinc-800/70 space-y-3 animate-in fade-in-50 duration-150">
          {children}
        </div>
      )}
    </div>
  );
};

const AlignLeftIcon = () => (
  <svg className="w-4 h-4 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <line x1="4" y1="2" x2="4" y2="22" strokeWidth="2.5" />
    <rect x="4" y="5" width="13" height="5" rx="1" fill="currentColor" fillOpacity="0.25" />
    <rect x="4" y="14" width="8" height="5" rx="1" fill="currentColor" fillOpacity="0.25" />
  </svg>
);

const AlignCenterHIcon = () => (
  <svg className="w-4 h-4 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <line x1="12" y1="2" x2="12" y2="22" strokeDasharray="2 2" strokeWidth="1.5" />
    <rect x="5" y="5" width="14" height="5" rx="1" fill="currentColor" fillOpacity="0.25" />
    <rect x="8" y="14" width="8" height="5" rx="1" fill="currentColor" fillOpacity="0.25" />
  </svg>
);

const AlignRightIcon = () => (
  <svg className="w-4 h-4 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <line x1="20" y1="2" x2="20" y2="22" strokeWidth="2.5" />
    <rect x="7" y="5" width="13" height="5" rx="1" fill="currentColor" fillOpacity="0.25" />
    <rect x="12" y="14" width="8" height="5" rx="1" fill="currentColor" fillOpacity="0.25" />
  </svg>
);

const AlignTopIcon = () => (
  <svg className="w-4 h-4 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <line x1="2" y1="4" x2="22" y2="4" strokeWidth="2.5" />
    <rect x="5" y="4" width="5" height="13" rx="1" fill="currentColor" fillOpacity="0.25" />
    <rect x="14" y="4" width="5" height="8" rx="1" fill="currentColor" fillOpacity="0.25" />
  </svg>
);

const AlignMiddleVIcon = () => (
  <svg className="w-4 h-4 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <line x1="2" y1="12" x2="22" y2="12" strokeDasharray="2 2" strokeWidth="1.5" />
    <rect x="5" y="5" width="5" height="14" rx="1" fill="currentColor" fillOpacity="0.25" />
    <rect x="14" y="8" width="5" height="8" rx="1" fill="currentColor" fillOpacity="0.25" />
  </svg>
);

const AlignBottomIcon = () => (
  <svg className="w-4 h-4 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <line x1="2" y1="20" x2="22" y2="20" strokeWidth="2.5" />
    <rect x="5" y="7" width="5" height="13" rx="1" fill="currentColor" fillOpacity="0.25" />
    <rect x="14" y="12" width="5" height="8" rx="1" fill="currentColor" fillOpacity="0.25" />
  </svg>
);

const DistributeHIcon = () => (
  <svg className="w-4 h-4 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <line x1="3" y1="3" x2="3" y2="21" strokeWidth="2" />
    <line x1="21" y1="3" x2="21" y2="21" strokeWidth="2" />
    <rect x="7" y="6" width="4" height="12" rx="1" fill="currentColor" fillOpacity="0.25" />
    <rect x="13" y="6" width="4" height="12" rx="1" fill="currentColor" fillOpacity="0.25" />
  </svg>
);

const DistributeVIcon = () => (
  <svg className="w-4 h-4 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <line x1="3" y1="3" x2="21" y2="3" strokeWidth="2" />
    <line x1="3" y1="21" x2="21" y2="21" strokeWidth="2" />
    <rect x="6" y="7" width="12" height="4" rx="1" fill="currentColor" fillOpacity="0.25" />
    <rect x="6" y="13" width="12" height="4" rx="1" fill="currentColor" fillOpacity="0.25" />
  </svg>
);

interface PropertiesPanelProps {
  selectedElements: VdpElement[];
  onUpdateElements: (updates: Partial<VdpElement>) => void;
  onBatchUpdateElements?: (updatesMap: Record<string, Partial<VdpElement>>) => void;
  canvasWidth?: number;
  canvasHeight?: number;
  appUnit: Unit;
  csvData: CSVData | null;
  previewRecordIndex?: number;
  onGroupSelected?: () => void;
  onUngroupSelected?: () => void;
  brandPalette?: BrandPalette;
  isModalMode?: boolean;
  onApplyPathfinder?: (mode: 'unite' | 'subtract' | 'intersect' | 'exclude' | 'divide' | 'merge') => void;
  onTogglePathfinderPanel?: () => void;
}

export const PropertiesPanel: React.FC<PropertiesPanelProps> = React.memo(({
  selectedElements,
  onUpdateElements,
  onBatchUpdateElements,
  canvasWidth,
  canvasHeight,
  appUnit,
  csvData,
  previewRecordIndex = 0,
  onGroupSelected,
  onUngroupSelected,
  brandPalette,
  isModalMode = false,
  onApplyPathfinder,
  onTogglePathfinderPanel
}) => {
  if (selectedElements.length === 0) return null;

  const [alignTo, setAlignTo] = useState<'selection' | 'canvas'>('selection');

  const [openSections, setOpenSections] = useState<Record<string, boolean>>(() => ({
    grouping: true,
    align: true,
    transform: true,
    appearance: true,
    effects: false,
    material: false,
    data: true,
    typography: true,
    dateSettings: true,
    currencySettings: true,
    imageSettings: true,
    qrSettings: true,
    barcodeSettings: true,
    shapeSettings: true,
    dynamicSettings: true,
    export: false
  }));

  const toggleSection = (key: string) => {
    setOpenSections(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const expandAllSections = () => {
    setOpenSections({
      grouping: true,
      align: true,
      transform: true,
      appearance: true,
      effects: true,
      material: true,
      data: true,
      typography: true,
      dateSettings: true,
      currencySettings: true,
      imageSettings: true,
      qrSettings: true,
      barcodeSettings: true,
      shapeSettings: true,
      dynamicSettings: true,
      export: true
    });
  };

  const collapseAllSections = () => {
    setOpenSections({
      grouping: false,
      align: false,
      transform: false,
      appearance: false,
      effects: false,
      material: false,
      data: false,
      typography: false,
      dateSettings: false,
      currencySettings: false,
      imageSettings: false,
      qrSettings: false,
      barcodeSettings: false,
      shapeSettings: false,
      dynamicSettings: false,
      export: false
    });
  };

  const sharedValues = React.useMemo(() => {
    if (selectedElements.length === 0) return {};
    
    const getSharedValue = <K extends keyof VdpElement>(key: K): VdpElement[K] | 'mixed' | undefined => {
      const firstVal = selectedElements[0][key];
      for (let i = 1; i < selectedElements.length; i++) {
        if (selectedElements[i][key] !== firstVal) return 'mixed' as any;
      }
      return firstVal;
    };

    const getSharedProp = <K extends keyof VdpElement>(...keys: K[]): VdpElement[K] | 'mixed' | undefined => {
      const getValue = (el: VdpElement) => {
        for (const k of keys) {
          if (el[k] !== undefined && el[k] !== null && el[k] !== '') return el[k];
        }
        return undefined;
      };
      const firstVal = getValue(selectedElements[0]);
      for (let i = 1; i < selectedElements.length; i++) {
        if (getValue(selectedElements[i]) !== firstVal) return 'mixed' as any;
      }
      return firstVal;
    };

    return {
      type: getSharedValue('type'),
      color: getSharedProp('color', 'fillColor'),
      borderColor: getSharedProp('borderColor', 'strokeColor'),
      borderWidth: getSharedProp('borderWidth', 'strokeWidth'),
      borderRadius: getSharedProp('borderRadius', 'cornerRadius'),
      fontFamily: getSharedValue('fontFamily'),
      fontSize: getSharedValue('fontSize'),
      isBold: getSharedValue('isBold'),
      isItalic: getSharedValue('isItalic'),
      textAlign: getSharedValue('textAlign'),
      opacity: getSharedValue('opacity'),
      qrErrorCorrection: getSharedValue('qrErrorCorrection'),
      qrStyle: getSharedValue('qrStyle'),
      showBarcodeText: getSharedValue('showBarcodeText'),
      letterSpacing: getSharedValue('letterSpacing'),
      lineHeight: getSharedValue('lineHeight'),
      horizontalScale: getSharedValue('horizontalScale'),
      verticalScale: getSharedValue('verticalScale'),
      baselineShift: getSharedValue('baselineShift'),
      textRotation: getSharedValue('textRotation'),
      textDecoration: getSharedValue('textDecoration'),
      verticalAlign: getSharedValue('verticalAlign'),
      textVariant: getSharedValue('textVariant'),
      prefix: getSharedValue('prefix'),
      suffix: getSharedValue('suffix'),
      textCase: getSharedValue('textCase'),
      autoShrink: getSharedValue('autoShrink'),
      isCurved: getSharedValue('isCurved'),
      curveRadius: getSharedValue('curveRadius'),
      dateFormat: getSharedValue('dateFormat'),
      disabled: getSharedValue('disabled'),
      isSkipped: getSharedValue('isSkipped'),
      currencySymbol: getSharedValue('currencySymbol'),
      decimalPlaces: getSharedValue('decimalPlaces'),
      formatThousands: getSharedValue('formatThousands'),
      imageSrc: getSharedProp('src', 'staticValue'),
      objectFit: getSharedValue('objectFit'),
      lineEffect: getSharedValue('lineEffect'),
      effectAmplitude: getSharedValue('effectAmplitude'),
      effectFrequency: getSharedValue('effectFrequency'),
      effectPointsStyle: getSharedValue('effectPointsStyle'),
      lineCap: getSharedValue('lineCap'),
      lineJoin: getSharedValue('lineJoin'),
      lineDash: getSharedValue('lineDash'),
      arrowheadStart: getSharedValue('arrowheadStart'),
      arrowheadEnd: getSharedValue('arrowheadEnd'),
      starPoints: getSharedValue('starPoints'),
      starInnerRadiusRatio: getSharedValue('starInnerRadiusRatio'),
      polygonSides: getSharedValue('polygonSides'),
      serialPrefix: getSharedValue('serialPrefix'),
      serialStart: getSharedValue('serialStart'),
      serialPadding: getSharedValue('serialPadding'),
      serialStep: getSharedValue('serialStep'),
      closedPath: getSharedValue('closedPath'),
      isSmooth: getSharedValue('isSmooth'),
      dropShadow: getSharedValue('dropShadow'),
      shadowColor: getSharedValue('shadowColor'),
      isCutline: getSharedValue('isCutline'),
    };
  }, [selectedElements]);

  const {
    type: sharedType, color, borderColor, borderWidth, borderRadius, fontFamily, fontSize, isBold, isItalic,
    textAlign, opacity, qrErrorCorrection, qrStyle, showBarcodeText, letterSpacing, lineHeight,
    horizontalScale, verticalScale, baselineShift, textRotation, textDecoration, verticalAlign,
    textVariant, prefix, suffix, textCase, autoShrink, isCurved, curveRadius, dateFormat,
    disabled, isSkipped, currencySymbol, decimalPlaces, formatThousands, imageSrc, objectFit,
    lineEffect, effectAmplitude, effectFrequency, effectPointsStyle, lineCap, lineJoin, lineDash,
    arrowheadStart, arrowheadEnd, starPoints, starInnerRadiusRatio, polygonSides,
    serialPrefix, serialStart, serialPadding, serialStep, closedPath, isSmooth,
    dropShadow, shadowColor, isCutline
  } = sharedValues;

  const isMixedType = sharedType === 'mixed';

  const getHexColor = (val: any, fallback: string = '#000000'): string => {
    if (!val || val === 'mixed' || val === 'transparent' || val === 'none') return fallback;
    if (typeof val === 'string' && val.startsWith('#')) {
      if (val.length === 7) return val;
      if (val.length === 4) return `#${val[1]}${val[1]}${val[2]}${val[2]}${val[3]}${val[3]}`;
    }
    return fallback;
  };

  const targetMode = selectedElements.length === 1 ? 'canvas' : alignTo;

  const [lastFillColor, setLastFillColor] = useState<string>('#2563eb');
  const [lastStrokeColor, setLastStrokeColor] = useState<string>('#ca8a04');
  const [activeColorTarget, setActiveColorTarget] = useState<'fill' | 'stroke'>('fill');

  const isFillEnabled = color !== 'none' && color !== 'transparent' && color !== '' && color !== undefined;
  const isStrokeEnabled = borderColor !== 'none' && borderColor !== 'transparent' && borderColor !== '' && borderColor !== undefined && (borderWidth === 'mixed' || (Number(borderWidth ?? 0) > 0));

  const handleSwapFillAndStroke = () => {
    const curFill = (color && color !== 'none' && color !== 'transparent' && color !== 'mixed') ? color : lastFillColor;
    const curStroke = (borderColor && borderColor !== 'none' && borderColor !== 'transparent' && borderColor !== 'mixed') ? borderColor : lastStrokeColor;

    const fillWasEnabled = isFillEnabled;
    const strokeWasEnabled = isStrokeEnabled;

    if (fillWasEnabled && strokeWasEnabled) {
      setLastFillColor(curStroke);
      setLastStrokeColor(curFill);
      onUpdateElements({ color: curStroke, fillColor: curStroke, borderColor: curFill, strokeColor: curFill });
    } else if (fillWasEnabled && !strokeWasEnabled) {
      setLastFillColor(curFill);
      setLastStrokeColor(curFill);
      onUpdateElements({
        color: 'transparent',
        fillColor: 'transparent',
        borderColor: curFill,
        strokeColor: curFill,
        borderWidth: (borderWidth && Number(borderWidth) > 0) ? borderWidth : 1,
        strokeWidth: (borderWidth && Number(borderWidth) > 0) ? borderWidth : 1
      });
    } else if (!fillWasEnabled && strokeWasEnabled) {
      setLastFillColor(curStroke);
      setLastStrokeColor(curStroke);
      onUpdateElements({
        color: curStroke,
        fillColor: curStroke,
        borderColor: 'transparent',
        strokeColor: 'transparent',
        borderWidth: 0,
        strokeWidth: 0
      });
    }
  };

  const handleToggleFill = (enable: boolean) => {
    if (!enable) {
      if (color && color !== 'none' && color !== 'transparent') {
        setLastFillColor(color);
      }
      onUpdateElements({ color: 'transparent', fillColor: 'transparent' });
    } else {
      const activeColor = (lastFillColor && lastFillColor !== 'none' && lastFillColor !== 'transparent') ? lastFillColor : '#2563eb';
      onUpdateElements({ color: activeColor, fillColor: activeColor });
    }
  };

  const handleToggleStroke = (enable: boolean) => {
    if (!enable) {
      if (borderColor && borderColor !== 'none' && borderColor !== 'transparent') {
        setLastStrokeColor(borderColor);
      }
      onUpdateElements({ borderColor: 'transparent', strokeColor: 'transparent', borderWidth: 0, strokeWidth: 0 });
    } else {
      const activeStroke = (lastStrokeColor && lastStrokeColor !== 'none' && lastStrokeColor !== 'transparent') ? lastStrokeColor : '#ca8a04';
      const activeWidth = (borderWidth && borderWidth !== 'mixed' && Number(borderWidth) > 0) ? Number(borderWidth) : 1;
      onUpdateElements({ borderColor: activeStroke, strokeColor: activeStroke, borderWidth: activeWidth, strokeWidth: activeWidth });
    }
  };

  const cWidth = canvasWidth || 300;
  const cHeight = canvasHeight || 200;

  const applyBatchUpdates = (updatesMap: Record<string, Partial<VdpElement>>) => {
    if (onBatchUpdateElements) {
      onBatchUpdateElements(updatesMap);
    } else {
      const combined: Partial<VdpElement> = {};
      Object.values(updatesMap).forEach(up => Object.assign(combined, up));
      onUpdateElements(combined);
    }
  };

  const handleAlignLeft = () => {
    if (selectedElements.length === 0) return;
    const updatesMap: Record<string, Partial<VdpElement>> = {};

    if (targetMode === 'canvas') {
      selectedElements.forEach(el => {
        updatesMap[el.id] = { x: 0 };
      });
    } else {
      const minX = Math.min(...selectedElements.map(e => e.x));
      selectedElements.forEach(el => {
        updatesMap[el.id] = { x: minX };
      });
    }
    applyBatchUpdates(updatesMap);
  };

  const handleAlignCenterH = () => {
    if (selectedElements.length === 0) return;
    const updatesMap: Record<string, Partial<VdpElement>> = {};

    if (targetMode === 'canvas') {
      selectedElements.forEach(el => {
        const w = el.width || 100;
        updatesMap[el.id] = { x: Math.round((cWidth - w) / 2) };
      });
    } else {
      const minX = Math.min(...selectedElements.map(e => e.x));
      const maxX = Math.max(...selectedElements.map(e => e.x + (e.width || 100)));
      const centerX = minX + (maxX - minX) / 2;
      selectedElements.forEach(el => {
        const w = el.width || 100;
        updatesMap[el.id] = { x: Math.round(centerX - w / 2) };
      });
    }
    applyBatchUpdates(updatesMap);
  };

  const handleAlignRight = () => {
    if (selectedElements.length === 0) return;
    const updatesMap: Record<string, Partial<VdpElement>> = {};

    if (targetMode === 'canvas') {
      selectedElements.forEach(el => {
        const w = el.width || 100;
        updatesMap[el.id] = { x: Math.round(cWidth - w) };
      });
    } else {
      const maxX = Math.max(...selectedElements.map(e => e.x + (e.width || 100)));
      selectedElements.forEach(el => {
        const w = el.width || 100;
        updatesMap[el.id] = { x: Math.round(maxX - w) };
      });
    }
    applyBatchUpdates(updatesMap);
  };

  const handleAlignTop = () => {
    if (selectedElements.length === 0) return;
    const updatesMap: Record<string, Partial<VdpElement>> = {};

    if (targetMode === 'canvas') {
      selectedElements.forEach(el => {
        updatesMap[el.id] = { y: 0 };
      });
    } else {
      const minY = Math.min(...selectedElements.map(e => e.y));
      selectedElements.forEach(el => {
        updatesMap[el.id] = { y: minY };
      });
    }
    applyBatchUpdates(updatesMap);
  };

  const handleAlignMiddleV = () => {
    if (selectedElements.length === 0) return;
    const updatesMap: Record<string, Partial<VdpElement>> = {};

    if (targetMode === 'canvas') {
      selectedElements.forEach(el => {
        const h = el.height || 100;
        updatesMap[el.id] = { y: Math.round((cHeight - h) / 2) };
      });
    } else {
      const minY = Math.min(...selectedElements.map(e => e.y));
      const maxY = Math.max(...selectedElements.map(e => e.y + (e.height || 100)));
      const centerY = minY + (maxY - minY) / 2;
      selectedElements.forEach(el => {
        const h = el.height || 100;
        updatesMap[el.id] = { y: Math.round(centerY - h / 2) };
      });
    }
    applyBatchUpdates(updatesMap);
  };

  const handleAlignBottom = () => {
    if (selectedElements.length === 0) return;
    const updatesMap: Record<string, Partial<VdpElement>> = {};

    if (targetMode === 'canvas') {
      selectedElements.forEach(el => {
        const h = el.height || 100;
        updatesMap[el.id] = { y: Math.round(cHeight - h) };
      });
    } else {
      const maxY = Math.max(...selectedElements.map(e => e.y + (e.height || 100)));
      selectedElements.forEach(el => {
        const h = el.height || 100;
        updatesMap[el.id] = { y: Math.round(maxY - h) };
      });
    }
    applyBatchUpdates(updatesMap);
  };

  const handleDistributeH = () => {
    if (selectedElements.length === 0) return;
    const sorted = [...selectedElements].sort((a, b) => a.x - b.x);
    const updatesMap: Record<string, Partial<VdpElement>> = {};

    if (targetMode === 'canvas') {
      const sumWidth = sorted.reduce((acc, el) => acc + (el.width || 100), 0);
      const freeSpace = cWidth - sumWidth;
      const gap = freeSpace / (sorted.length + 1);
      let currX = gap;
      sorted.forEach(el => {
        updatesMap[el.id] = { x: Math.round(currX) };
        currX += (el.width || 100) + gap;
      });
    } else {
      if (sorted.length < 2) return;
      const minX = sorted[0].x;
      const maxRight = sorted[sorted.length - 1].x + (sorted[sorted.length - 1].width || 100);
      const sumWidth = sorted.reduce((acc, el) => acc + (el.width || 100), 0);
      const freeSpace = (maxRight - minX) - sumWidth;
      const gap = freeSpace / (sorted.length - 1);
      let currX = minX;
      sorted.forEach((el, idx) => {
        if (idx === 0) {
          updatesMap[el.id] = { x: minX };
        } else if (idx === sorted.length - 1) {
          updatesMap[el.id] = { x: maxRight - (el.width || 100) };
        } else {
          updatesMap[el.id] = { x: Math.round(currX) };
        }
        currX += (el.width || 100) + gap;
      });
    }
    applyBatchUpdates(updatesMap);
  };

  const handleDistributeV = () => {
    if (selectedElements.length === 0) return;
    const sorted = [...selectedElements].sort((a, b) => a.y - b.y);
    const updatesMap: Record<string, Partial<VdpElement>> = {};

    if (targetMode === 'canvas') {
      const sumHeight = sorted.reduce((acc, el) => acc + (el.height || 100), 0);
      const freeSpace = cHeight - sumHeight;
      const gap = freeSpace / (sorted.length + 1);
      let currY = gap;
      sorted.forEach(el => {
        updatesMap[el.id] = { y: Math.round(currY) };
        currY += (el.height || 100) + gap;
      });
    } else {
      if (sorted.length < 2) return;
      const minY = sorted[0].y;
      const maxBottom = sorted[sorted.length - 1].y + (sorted[sorted.length - 1].height || 100);
      const sumHeight = sorted.reduce((acc, el) => acc + (el.height || 100), 0);
      const freeSpace = (maxBottom - minY) - sumHeight;
      const gap = freeSpace / (sorted.length - 1);
      let currY = minY;
      sorted.forEach((el, idx) => {
        if (idx === 0) {
          updatesMap[el.id] = { y: minY };
        } else if (idx === sorted.length - 1) {
          updatesMap[el.id] = { y: maxBottom - (el.height || 100) };
        } else {
          updatesMap[el.id] = { y: Math.round(currY) };
        }
        currY += (el.height || 100) + gap;
      });
    }
    applyBatchUpdates(updatesMap);
  };

  const handleCenterCanvasBoth = () => {
    if (selectedElements.length === 0) return;
    const updatesMap: Record<string, Partial<VdpElement>> = {};

    if (selectedElements.length === 1) {
      const el = selectedElements[0];
      const w = el.width || 100;
      const h = el.height || 100;
      updatesMap[el.id] = {
        x: Math.round((cWidth - w) / 2),
        y: Math.round((cHeight - h) / 2)
      };
    } else {
      const minX = Math.min(...selectedElements.map(e => e.x));
      const maxX = Math.max(...selectedElements.map(e => e.x + (e.width || 100)));
      const minY = Math.min(...selectedElements.map(e => e.y));
      const maxY = Math.max(...selectedElements.map(e => e.y + (e.height || 100)));

      const groupW = maxX - minX;
      const groupH = maxY - minY;

      const targetMinX = (cWidth - groupW) / 2;
      const targetMinY = (cHeight - groupH) / 2;

      const dx = targetMinX - minX;
      const dy = targetMinY - minY;

      selectedElements.forEach(el => {
        updatesMap[el.id] = {
          x: Math.round(el.x + dx),
          y: Math.round(el.y + dy)
        };
      });
    }
    applyBatchUpdates(updatesMap);
  };

  const renderColumn1 = () => (
    <div className="space-y-3">
      {/* Grouping Section */}
      {(selectedElements.length >= 2 || selectedElements.some(e => Boolean(e.groupId))) && (
        <AccordionSection
          id="grouping"
          title={selectedElements.some(e => Boolean(e.groupId)) ? 'Grouped Unit' : 'Element Grouping'}
          icon={<FolderPlus className="w-3.5 h-3.5 text-blue-400" />}
          badge={<span className="text-[10px] text-zinc-400 font-mono">({selectedElements.length} items)</span>}
          isOpen={openSections.grouping}
          onToggle={() => toggleSection('grouping')}
          className="border-blue-900/50 bg-blue-950/20"
        >
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-[11px] text-blue-300 font-medium">Batch Selection Group</span>
              <span className="text-[10px] text-zinc-400 font-mono">{selectedElements.length} items</span>
            </div>
            <div className="flex items-center gap-2">
              {selectedElements.length >= 2 && onGroupSelected && (
                <button
                  type="button"
                  onClick={onGroupSelected}
                  className="flex-1 py-1.5 px-3 bg-blue-600 hover:bg-blue-500 text-white rounded text-xs font-medium transition-colors flex items-center justify-center gap-1.5 shadow-sm cursor-pointer"
                >
                  <FolderPlus className="w-3.5 h-3.5" /> Group (Ctrl+G)
                </button>
              )}
              {selectedElements.some(e => Boolean(e.groupId)) && onUngroupSelected && (
                <button
                  type="button"
                  onClick={onUngroupSelected}
                  className="flex-1 py-1.5 px-3 bg-amber-600/80 hover:bg-amber-500 text-white rounded text-xs font-medium transition-colors flex items-center justify-center gap-1.5 shadow-sm cursor-pointer"
                >
                  <FolderMinus className="w-3.5 h-3.5" /> Ungroup (Ctrl+Shift+G)
                </button>
              )}
            </div>
          </div>
        </AccordionSection>
      )}

      {/* Align & Distribute Section */}
      <AccordionSection
        id="align"
        title="Align & Distribute"
        icon={<LayoutGrid className="w-3.5 h-3.5 text-blue-400" />}
        badge={selectedElements.length > 1 ? (
          <div className="flex items-center bg-zinc-950 border border-zinc-700/80 rounded p-0.5 text-[10px]" onClick={e => e.stopPropagation()}>
            <button
              type="button"
              onClick={() => setAlignTo('selection')}
              className={`px-1.5 py-0.5 rounded transition-colors ${alignTo === 'selection' ? 'bg-blue-600 text-white font-medium' : 'text-zinc-400 hover:text-zinc-200'}`}
            >
              Sel
            </button>
            <button
              type="button"
              onClick={() => setAlignTo('canvas')}
              className={`px-1.5 py-0.5 rounded transition-colors ${alignTo === 'canvas' ? 'bg-blue-600 text-white font-medium' : 'text-zinc-400 hover:text-zinc-200'}`}
            >
              Canvas
            </button>
          </div>
        ) : undefined}
        isOpen={openSections.align}
        onToggle={() => toggleSection('align')}
      >
        <div className="space-y-3">
          {/* Horizontal Alignment */}
          <div>
            <span className="text-[11px] text-zinc-400 font-medium mb-1 block">Horizontal Alignment</span>
            <div className="grid grid-cols-3 gap-1.5">
              <button
                onClick={handleAlignLeft}
                className="flex items-center justify-center gap-1 py-1.5 bg-zinc-900 hover:bg-zinc-800 border border-zinc-700/80 hover:border-zinc-600 rounded text-xs text-zinc-200 transition-colors"
                title="Align Left"
              >
                <AlignLeftIcon />
                <span>Left</span>
              </button>
              <button
                onClick={handleAlignCenterH}
                className="flex items-center justify-center gap-1 py-1.5 bg-zinc-900 hover:bg-zinc-800 border border-zinc-700/80 hover:border-zinc-600 rounded text-xs text-zinc-200 transition-colors"
                title="Align Center (Horizontal)"
              >
                <AlignCenterHIcon />
                <span>Center</span>
              </button>
              <button
                onClick={handleAlignRight}
                className="flex items-center justify-center gap-1 py-1.5 bg-zinc-900 hover:bg-zinc-800 border border-zinc-700/80 hover:border-zinc-600 rounded text-xs text-zinc-200 transition-colors"
                title="Align Right"
              >
                <AlignRightIcon />
                <span>Right</span>
              </button>
            </div>
          </div>

          {/* Vertical Alignment */}
          <div>
            <span className="text-[11px] text-zinc-400 font-medium mb-1 block">Vertical Alignment</span>
            <div className="grid grid-cols-3 gap-1.5">
              <button
                onClick={handleAlignTop}
                className="flex items-center justify-center gap-1 py-1.5 bg-zinc-900 hover:bg-zinc-800 border border-zinc-700/80 hover:border-zinc-600 rounded text-xs text-zinc-200 transition-colors"
                title="Align Top"
              >
                <AlignTopIcon />
                <span>Top</span>
              </button>
              <button
                onClick={handleAlignMiddleV}
                className="flex items-center justify-center gap-1 py-1.5 bg-zinc-900 hover:bg-zinc-800 border border-zinc-700/80 hover:border-zinc-600 rounded text-xs text-zinc-200 transition-colors"
                title="Align Middle (Vertical)"
              >
                <AlignMiddleVIcon />
                <span>Middle</span>
              </button>
              <button
                onClick={handleAlignBottom}
                className="flex items-center justify-center gap-1 py-1.5 bg-zinc-900 hover:bg-zinc-800 border border-zinc-700/80 hover:border-zinc-600 rounded text-xs text-zinc-200 transition-colors"
                title="Align Bottom"
              >
                <AlignBottomIcon />
                <span>Bottom</span>
              </button>
            </div>
          </div>

          {/* Distribute Space */}
          <div>
            <span className="text-[11px] text-zinc-400 font-medium mb-1 block">Distribute Evenly</span>
            <div className="grid grid-cols-2 gap-1.5">
              <button
                onClick={handleDistributeH}
                disabled={selectedElements.length < 2 && targetMode !== 'canvas'}
                className="flex items-center justify-center gap-1 py-1.5 bg-zinc-900 hover:bg-zinc-800 border border-zinc-700/80 hover:border-zinc-600 rounded text-xs text-zinc-200 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                title="Distribute Horizontally"
              >
                <DistributeHIcon />
                <span>Horizontally</span>
              </button>
              <button
                onClick={handleDistributeV}
                disabled={selectedElements.length < 2 && targetMode !== 'canvas'}
                className="flex items-center justify-center gap-1 py-1.5 bg-zinc-900 hover:bg-zinc-800 border border-zinc-700/80 hover:border-zinc-600 rounded text-xs text-zinc-200 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                title="Distribute Vertically"
              >
                <DistributeVIcon />
                <span>Vertically</span>
              </button>
            </div>
          </div>

          {/* Center on Canvas */}
          <button
            onClick={handleCenterCanvasBoth}
            className="w-full py-1.5 bg-blue-600/15 hover:bg-blue-600/25 text-blue-400 border border-blue-500/30 rounded text-xs font-medium transition-colors flex items-center justify-center gap-1.5 cursor-pointer"
          >
            <Maximize2 className="w-3.5 h-3.5" />
            <span>Center on Canvas</span>
          </button>
        </div>
      </AccordionSection>

      {/* Position, Size & Rotation */}
      <AccordionSection
        id="transform"
        title={selectedElements.length === 1 ? "Position, Size & Rotation" : "Transform Multi-Selection"}
        icon={<Maximize2 className="w-3.5 h-3.5 text-blue-400" />}
        badge={selectedElements.length === 1 ? (
          <span className="text-[10px] text-zinc-400 font-mono">
            {formatUnit(selectedElements[0].x, appUnit)} × {formatUnit(selectedElements[0].y, appUnit)}
          </span>
        ) : undefined}
        isOpen={openSections.transform}
        onToggle={() => toggleSection('transform')}
      >
        {selectedElements.length === 1 ? (
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium text-zinc-500 mb-1 block">X ({appUnit})</label>
              <input type="number" className="w-full px-3 py-1.5 border border-zinc-700 rounded text-sm bg-zinc-900 text-zinc-200" value={formatUnit(selectedElements[0].x, appUnit)} onChange={e => onUpdateElements({ x: parseUnit(e.target.value, appUnit) })} />
            </div>
            <div>
              <label className="text-xs font-medium text-zinc-500 mb-1 block">Y ({appUnit})</label>
              <input type="number" className="w-full px-3 py-1.5 border border-zinc-700 rounded text-sm bg-zinc-900 text-zinc-200" value={formatUnit(selectedElements[0].y, appUnit)} onChange={e => onUpdateElements({ y: parseUnit(e.target.value, appUnit) })} />
            </div>
            <div>
              <label className="text-xs font-medium text-zinc-500 mb-1 block">Width ({appUnit})</label>
              <input type="number" className="w-full px-3 py-1.5 border border-zinc-700 rounded text-sm bg-zinc-900 text-zinc-200" value={formatUnit(selectedElements[0].width || 100, appUnit)} onChange={e => onUpdateElements({ width: parseUnit(e.target.value, appUnit) })} />
            </div>
            <div>
              <label className="text-xs font-medium text-zinc-500 mb-1 block">Height ({appUnit})</label>
              <input type="number" className="w-full px-3 py-1.5 border border-zinc-700 rounded text-sm bg-zinc-900 text-zinc-200" value={formatUnit(selectedElements[0].height || 100, appUnit)} onChange={e => onUpdateElements({ height: parseUnit(e.target.value, appUnit) })} />
            </div>
            <div>
              <label className="text-xs font-medium text-zinc-500 mb-1 block">Rotation (°)</label>
              <input type="number" className="w-full px-3 py-1.5 border border-zinc-700 rounded text-sm bg-zinc-900 text-zinc-200" value={selectedElements[0].rotation || 0} onChange={e => onUpdateElements({ rotation: parseFloat(e.target.value) || 0 })} />
            </div>
            <div>
              <label className="text-xs font-medium text-zinc-500 mb-1 block">Opacity (%)</label>
              <input type="number" min="0" max="100" className="w-full px-3 py-1.5 border border-zinc-700 rounded text-sm bg-zinc-900 text-zinc-200" value={Math.round((selectedElements[0].opacity ?? 1) * 100)} onChange={e => onUpdateElements({ opacity: Math.max(0, Math.min(1, (parseFloat(e.target.value) || 0) / 100)) })} />
            </div>
          </div>
        ) : (
          <GroupSelectionControls
            selectedElements={selectedElements}
            appUnit={appUnit}
            onUpdateElements={onUpdateElements}
            applyBatchUpdates={applyBatchUpdates}
            onApplyPathfinder={onApplyPathfinder}
            onTogglePathfinderPanel={onTogglePathfinderPanel}
          />
        )}
      </AccordionSection>
    </div>
  );

  const renderColumn2 = () => (
    <div className="space-y-3">
      {/* Illustrator / Photoshop Style Fill & Stroke Controls */}
      <AccordionSection
        id="appearance"
        title="Fill, Stroke & Borders"
        icon={<Palette className="w-3.5 h-3.5 text-indigo-400" />}
        badge={
          <span className="text-[10px] text-zinc-400 font-mono">
            {activeColorTarget.toUpperCase()}
          </span>
        }
        isOpen={openSections.appearance}
        onToggle={() => toggleSection('appearance')}
      >
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold text-zinc-300">Fill & Stroke Palette</span>
            <span className="text-[10px] text-zinc-500 font-mono">
              Active: <span className="text-blue-400 font-bold uppercase">{activeColorTarget}</span>
            </span>
          </div>

          {/* Swatch Box + Color Picker & Inputs */}
          <div className="bg-zinc-950 p-2.5 rounded-lg border border-zinc-800 space-y-2.5">
            <div className="flex items-center gap-3">
              {/* Illustrator Style Overlapping Swatches + Swap Arrow */}
              <div className="relative w-16 h-16 bg-zinc-900 border border-zinc-800 rounded-lg shrink-0 flex items-center justify-center select-none shadow-inner">
                {/* Stroke Swatch (Back) */}
                <div
                  onClick={() => setActiveColorTarget('stroke')}
                  className={`absolute top-1.5 right-1.5 w-8 h-8 rounded border-2 cursor-pointer transition-all ${
                    activeColorTarget === 'stroke' ? 'border-blue-500 ring-2 ring-blue-500/30 z-10 scale-105' : 'border-zinc-700 z-0 hover:border-zinc-500'
                  }`}
                  style={{
                    backgroundColor: isStrokeEnabled ? (borderColor === 'mixed' ? '#888888' : (borderColor || '#ca8a04')) : '#ffffff'
                  }}
                  title="Stroke Color (Click to select)"
                >
                  {!isStrokeEnabled && (
                    <div className="w-full h-full relative overflow-hidden bg-white">
                      <div className="absolute inset-0 m-auto w-[120%] h-[3px] bg-red-600 -rotate-45" />
                    </div>
                  )}
                </div>

                {/* Fill Swatch (Front) */}
                <div
                  onClick={() => setActiveColorTarget('fill')}
                  className={`absolute bottom-1.5 left-1.5 w-8 h-8 rounded border-2 cursor-pointer transition-all ${
                    activeColorTarget === 'fill' ? 'border-blue-500 ring-2 ring-blue-500/30 z-20 scale-105' : 'border-zinc-600 z-10 hover:border-zinc-400 shadow-md'
                  }`}
                  style={{
                    backgroundColor: isFillEnabled ? (color === 'mixed' ? '#888888' : (color || '#2563eb')) : '#ffffff'
                  }}
                  title="Fill Color (Click to select)"
                >
                  {!isFillEnabled && (
                    <div className="w-full h-full relative overflow-hidden bg-white">
                      <div className="absolute inset-0 m-auto w-[120%] h-[3px] bg-red-600 -rotate-45" />
                    </div>
                  )}
                </div>

                {/* Swap Fill/Stroke Button */}
                <button
                  onClick={handleSwapFillAndStroke}
                  className="absolute top-1 left-1 p-0.5 rounded bg-zinc-800/90 hover:bg-zinc-700 text-zinc-300 hover:text-white transition-colors z-30 cursor-pointer"
                  title="Swap Fill and Stroke"
                >
                  <svg className="w-3 h-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                    <path d="M7 10l5-5 5 5M17 14l-5 5-5-5" />
                  </svg>
                </button>
              </div>

              {/* Active Target Details */}
              <div className="flex-1 space-y-1.5">
                <div className="flex items-center justify-between text-xs">
                  <span className="font-medium text-zinc-300">
                    {activeColorTarget === 'fill' ? 'Fill Color' : 'Stroke Color'}
                  </span>
                  <label className="flex items-center gap-1.5 text-[10px] text-zinc-400 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={activeColorTarget === 'fill' ? isFillEnabled : isStrokeEnabled}
                      onChange={e => activeColorTarget === 'fill' ? handleToggleFill(e.target.checked) : handleToggleStroke(e.target.checked)}
                      className="rounded border-zinc-700 bg-zinc-900 text-blue-600 focus:ring-0 w-3 h-3"
                    />
                    <span>Enabled</span>
                  </label>
                </div>

                <div className="flex items-center gap-1.5">
                  <input
                    type="color"
                    disabled={activeColorTarget === 'fill' ? !isFillEnabled : !isStrokeEnabled}
                    className="w-8 h-8 p-0 border-0 rounded cursor-pointer shrink-0 bg-transparent disabled:opacity-30"
                    value={
                      activeColorTarget === 'fill'
                        ? (color === 'mixed' ? '#888888' : (!isFillEnabled ? (lastFillColor || '#2563eb') : (color || '#2563eb')))
                        : (borderColor === 'mixed' ? '#888888' : (!isStrokeEnabled ? (lastStrokeColor || '#ca8a04') : (borderColor || '#ca8a04')))
                    }
                    onChange={e => {
                      if (activeColorTarget === 'fill') {
                        setLastFillColor(e.target.value);
                        onUpdateElements({ color: e.target.value, fillColor: e.target.value });
                      } else {
                        setLastStrokeColor(e.target.value);
                        onUpdateElements({ borderColor: e.target.value, strokeColor: e.target.value });
                      }
                    }}
                  />
                  <input
                    type="text"
                    disabled={activeColorTarget === 'fill' ? !isFillEnabled : !isStrokeEnabled}
                    className="w-full px-2 py-1.5 border border-zinc-700 rounded text-xs bg-zinc-900 text-zinc-200 disabled:bg-zinc-950 disabled:text-zinc-600 font-mono"
                    value={
                      activeColorTarget === 'fill'
                        ? (color === 'mixed' ? 'Mixed' : (!isFillEnabled ? 'Transparent' : (color || '#2563eb')))
                        : (borderColor === 'mixed' ? 'Mixed' : (!isStrokeEnabled ? 'Transparent' : (borderColor || '#ca8a04')))
                    }
                    onChange={e => {
                      if (activeColorTarget === 'fill') {
                        setLastFillColor(e.target.value);
                        onUpdateElements({ color: e.target.value, fillColor: e.target.value });
                      } else {
                        setLastStrokeColor(e.target.value);
                        onUpdateElements({ borderColor: e.target.value, strokeColor: e.target.value });
                      }
                    }}
                  />
                </div>

                {/* Quick Brand Colors Palette Swatches */}
                {brandPalette && (
                  <div className="flex items-center gap-1.5 pt-2 flex-wrap">
                    <span className="text-[10px] font-semibold text-zinc-400">Brand Swatches:</span>
                    <button
                      type="button"
                      onClick={() => {
                        if (activeColorTarget === 'fill') {
                          setLastFillColor(brandPalette.primary);
                          onUpdateElements({ color: brandPalette.primary, fillColor: brandPalette.primary });
                        } else {
                          setLastStrokeColor(brandPalette.primary);
                          onUpdateElements({ borderColor: brandPalette.primary, strokeColor: brandPalette.primary });
                        }
                      }}
                      className="w-4 h-4 rounded-full border border-zinc-600 hover:scale-125 transition-transform cursor-pointer shadow-xs"
                      style={{ backgroundColor: brandPalette.primary }}
                      title={`Primary Brand: ${brandPalette.primary}`}
                    ></button>
                    <button
                      type="button"
                      onClick={() => {
                        if (activeColorTarget === 'fill') {
                          setLastFillColor(brandPalette.secondary);
                          onUpdateElements({ color: brandPalette.secondary, fillColor: brandPalette.secondary });
                        } else {
                          setLastStrokeColor(brandPalette.secondary);
                          onUpdateElements({ borderColor: brandPalette.secondary, strokeColor: brandPalette.secondary });
                        }
                      }}
                      className="w-4 h-4 rounded-full border border-zinc-600 hover:scale-125 transition-transform cursor-pointer shadow-xs"
                      style={{ backgroundColor: brandPalette.secondary }}
                      title={`Secondary Brand: ${brandPalette.secondary}`}
                    ></button>
                    {brandPalette.accent && (
                      <button
                        type="button"
                        onClick={() => {
                          if (activeColorTarget === 'fill') {
                            setLastFillColor(brandPalette.accent!);
                            onUpdateElements({ color: brandPalette.accent, fillColor: brandPalette.accent });
                          } else {
                            setLastStrokeColor(brandPalette.accent!);
                            onUpdateElements({ borderColor: brandPalette.accent, strokeColor: brandPalette.accent });
                          }
                        }}
                        className="w-4 h-4 rounded-full border border-zinc-600 hover:scale-125 transition-transform cursor-pointer shadow-xs"
                        style={{ backgroundColor: brandPalette.accent }}
                        title={`Accent Brand: ${brandPalette.accent}`}
                      ></button>
                    )}
                    {brandPalette.neutral && (
                      <button
                        type="button"
                        onClick={() => {
                          if (activeColorTarget === 'fill') {
                            setLastFillColor(brandPalette.neutral!);
                            onUpdateElements({ color: brandPalette.neutral, fillColor: brandPalette.neutral });
                          } else {
                            setLastStrokeColor(brandPalette.neutral!);
                            onUpdateElements({ borderColor: brandPalette.neutral, strokeColor: brandPalette.neutral });
                          }
                        }}
                        className="w-4 h-4 rounded-full border border-zinc-600 hover:scale-125 transition-transform cursor-pointer shadow-xs"
                        style={{ backgroundColor: brandPalette.neutral }}
                        title={`Neutral Brand: ${brandPalette.neutral}`}
                      ></button>
                    )}
                    {brandPalette.swatches?.map(sw => (
                      <button
                        key={sw.id}
                        type="button"
                        onClick={() => {
                          if (activeColorTarget === 'fill') {
                            setLastFillColor(sw.hex);
                            onUpdateElements({ color: sw.hex, fillColor: sw.hex });
                          } else {
                            setLastStrokeColor(sw.hex);
                            onUpdateElements({ borderColor: sw.hex, strokeColor: sw.hex });
                          }
                        }}
                        className="w-4 h-4 rounded-full border border-zinc-600 hover:scale-125 transition-transform cursor-pointer shadow-xs"
                        style={{ backgroundColor: sw.hex }}
                        title={`${sw.name}: ${sw.hex}`}
                      ></button>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Illustrator Style 3 Mode Buttons: [Solid] [Gradient] [None / Red Slash] */}
            <div className="grid grid-cols-3 gap-1.5 pt-2 border-t border-zinc-800/80">
              {/* Solid Color */}
              <button
                onClick={() => {
                  if (activeColorTarget === 'fill') handleToggleFill(true);
                  else handleToggleStroke(true);
                }}
                className={`flex items-center justify-center gap-1.5 py-1.5 px-2 rounded border text-[11px] font-medium transition-colors cursor-pointer ${
                  (activeColorTarget === 'fill' ? isFillEnabled : isStrokeEnabled)
                    ? 'bg-blue-600/20 text-blue-300 border-blue-500/40'
                    : 'bg-zinc-900 text-zinc-400 border-zinc-800 hover:bg-zinc-800'
                }`}
              >
                <div className="w-3 h-3 rounded-sm bg-zinc-200 border border-zinc-400" />
                <span>Solid</span>
              </button>

              {/* Gradient */}
              <button
                onClick={() => {
                  const gradColor = '#3b82f6';
                  if (activeColorTarget === 'fill') {
                    setLastFillColor(gradColor);
                    onUpdateElements({ color: gradColor, fillColor: gradColor });
                  } else {
                    setLastStrokeColor(gradColor);
                    onUpdateElements({ borderColor: gradColor, strokeColor: gradColor });
                  }
                }}
                className="flex items-center justify-center gap-1.5 py-1.5 px-2 rounded border bg-zinc-900 text-zinc-400 border-zinc-800 hover:bg-zinc-800 text-[11px] font-medium transition-colors cursor-pointer"
              >
                <div className="w-3 h-3 rounded-sm bg-gradient-to-r from-blue-500 via-indigo-500 to-purple-500" />
                <span>Gradient</span>
              </button>

              {/* None / Disabled (Red Slash /) */}
              <button
                onClick={() => {
                  if (activeColorTarget === 'fill') handleToggleFill(false);
                  else handleToggleStroke(false);
                }}
                className={`flex items-center justify-center gap-1.5 py-1.5 px-2 rounded border text-[11px] font-medium transition-colors cursor-pointer ${
                  !(activeColorTarget === 'fill' ? isFillEnabled : isStrokeEnabled)
                    ? 'bg-red-500/20 text-red-300 border-red-500/40'
                    : 'bg-zinc-900 text-zinc-400 border-zinc-800 hover:bg-zinc-800'
                }`}
              >
                <div className="w-3 h-3 rounded-sm bg-white border border-zinc-400 relative overflow-hidden shrink-0">
                  <div className="absolute inset-0 m-auto w-[140%] h-[2px] bg-red-600 -rotate-45" />
                </div>
                <span>None</span>
              </button>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium text-zinc-500 mb-1 block">Border Width (pt)</label>
              <input type="number" step="0.5" min="0" className="w-full px-3 py-1.5 border border-zinc-700 rounded text-sm bg-zinc-900 text-zinc-200" value={borderWidth === 'mixed' ? '' : (borderWidth ?? 0)} placeholder={borderWidth === 'mixed' ? 'Mixed' : ''} onChange={e => { const val = parseFloat(e.target.value); const safeVal = isNaN(val) ? 0 : val; onUpdateElements({ borderWidth: safeVal, strokeWidth: safeVal }); }} />
            </div>
            <div>
              <label className="text-xs font-medium text-zinc-500 mb-1 block">Corner Radius (px)</label>
              <input type="number" min="0" className="w-full px-3 py-1.5 border border-zinc-700 rounded text-sm bg-zinc-900 text-zinc-200" value={borderRadius === 'mixed' ? '' : (borderRadius ?? 0)} placeholder={borderRadius === 'mixed' ? 'Mixed' : ''} onChange={e => { const val = parseFloat(e.target.value); const safeVal = isNaN(val) ? 0 : val; onUpdateElements({ borderRadius: safeVal, cornerRadius: safeVal }); }} />
            </div>
          </div>
        </div>
      </AccordionSection>

      {/* Drop Shadow & Cutline Options */}
      <AccordionSection
        id="effects"
        title="Drop Shadow & Cutline"
        icon={<Sparkles className="w-3.5 h-3.5 text-amber-400" />}
        badge={selectedElements[0]?.dropShadow ? <span className="text-[10px] text-amber-400 font-medium">Shadow ON</span> : undefined}
        isOpen={openSections.effects}
        onToggle={() => toggleSection('effects')}
      >
        <div className="grid grid-cols-2 gap-3">
          <label className="flex items-center gap-2 text-xs text-zinc-300 cursor-pointer">
            <input
              type="checkbox"
              checked={selectedElements[0]?.dropShadow || false}
              onChange={e => onUpdateElements({ dropShadow: e.target.checked })}
              className="rounded border-zinc-700 text-blue-600"
            />
            <span>Drop Shadow</span>
          </label>
          {selectedElements[0]?.dropShadow && (
            <div className="flex items-center gap-1.5">
              <input
                type="color"
                value={selectedElements[0]?.shadowColor || 'rgba(0,0,0,0.4)'}
                onChange={e => onUpdateElements({ shadowColor: e.target.value })}
                className="w-6 h-6 border-0 rounded bg-transparent cursor-pointer"
              />
              <span className="text-[10px] text-zinc-400 font-mono">Shadow Color</span>
            </div>
          )}
          {['rectangle', 'circle', 'star', 'polygon', 'line', 'path', 'pen'].includes(sharedType as string) && (
            <label className="flex items-center gap-2 text-xs text-amber-400 font-medium cursor-pointer col-span-2">
              <input
                type="checkbox"
                checked={selectedElements[0]?.isCutline || false}
                onChange={e => onUpdateElements({ isCutline: e.target.checked, borderColor: e.target.checked ? '#ff007f' : selectedElements[0]?.borderColor })}
                className="rounded border-amber-600 text-amber-500"
              />
              <span>Die-Cut Line / Kiss-Cut Spot Path (Magenta)</span>
            </label>
          )}
        </div>
      </AccordionSection>

      {/* Material Properties & Tactile Finishes (Roughness, Metallic, Bump Maps, Embossing) */}
      {selectedElements.length === 1 && (
        <AccordionSection
          id="material"
          title="Material & Tactile Finishes"
          icon={<Box className="w-3.5 h-3.5 text-emerald-400" />}
          isOpen={openSections.material}
          onToggle={() => toggleSection('material')}
        >
          <MaterialDetailSettingsPanel
            selectedElements={selectedElements}
            onUpdateElements={onUpdateElements}
            title="Material & Tactile Finishes"
          />
        </AccordionSection>
      )}
    </div>
  );

  const renderColumn3 = () => (
    <div className="space-y-3">
      {/* Mapping & Static Value */}
      {selectedElements.length === 1 && (
        <AccordionSection
          id="data"
          title="Data Mapping & Content"
          icon={<FileCode className="w-3.5 h-3.5 text-blue-400" />}
          badge={selectedElements[0].columnName ? (
            <span className="text-[10px] px-2 py-0.5 rounded bg-blue-900/60 text-blue-300 font-medium border border-blue-700/50 flex items-center gap-1">
              <span className="w-1.5 h-1.5 rounded-full bg-blue-400"></span>
              {selectedElements[0].columnName}
            </span>
          ) : (
            <span className="text-[10px] px-2 py-0.5 rounded bg-zinc-800 text-zinc-400 font-medium">
              Static
            </span>
          )}
          isOpen={openSections.data}
          onToggle={() => toggleSection('data')}
        >
          <div className="space-y-3">
            <div>
              <label className="text-xs font-medium text-zinc-400 mb-1 block">Map to CSV Column</label>
              <select 
                className="w-full px-3 py-2 border border-zinc-700 rounded text-sm bg-zinc-900 focus:ring-2 focus:ring-blue-600 outline-none text-zinc-300"
                value={selectedElements[0].columnName || ''}
                onChange={e => {
                  const col = e.target.value;
                  if (!col) {
                    const fallbackVal = selectedElements[0].content || selectedElements[0].text || selectedElements[0].staticValue || selectedElements[0].columnName || 'Sample Text';
                    onUpdateElements({ columnName: '', isStatic: true, content: fallbackVal, text: fallbackVal, staticValue: fallbackVal });
                  } else {
                    onUpdateElements({ columnName: col, isStatic: false });
                  }
                }}
              >
                <option value="">-- Static / Unmapped --</option>
                {csvData?.headers.map(h => (
                  <option key={h} value={h}>{h}</option>
                ))}
              </select>
            </div>
            {!selectedElements[0].columnName && ['text', 'uppercase', 'barcode', 'qr', 'datamatrix', 'ean13', 'upca', 'currency', 'date', 'serial', 'curved-text'].includes(sharedType as string) && (
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="text-xs font-medium text-zinc-400 block">Text Content / Static Value</label>
                  <span className="text-[10px] text-zinc-400 font-normal">Shown on canvas &amp; print</span>
                </div>
                <input 
                  type="text"
                  className="w-full px-3 py-1.5 border border-zinc-700 rounded text-sm bg-zinc-900 focus:ring-2 focus:ring-blue-600 outline-none text-zinc-200"
                  value={selectedElements[0].content ?? selectedElements[0].text ?? selectedElements[0].staticValue ?? ''}
                  onChange={e => {
                    const val = e.target.value;
                    onUpdateElements({ content: val, text: val, staticValue: val, isStatic: true });
                  }}
                  placeholder="Enter text (e.g. Sample Text)"
                />
                <p className="text-[11px] text-zinc-500 mt-1">
                  This content or placeholder text stays clearly visible on the layout and imposition sheet.
                </p>
              </div>
            )}
            {selectedElements[0].columnName && (
              <label className="flex items-center gap-2 text-xs text-zinc-400 cursor-pointer">
                <input 
                  type="checkbox" 
                  checked={selectedElements[0].hideIfEmpty || false} 
                  onChange={e => onUpdateElements({ hideIfEmpty: e.target.checked })} 
                  className="rounded border-zinc-700"
                />
                <span>Hide if data is empty</span>
              </label>
            )}
          </div>
        </AccordionSection>
      )}

      {/* Typography & Text Formatting */}
      {(!isMixedType && ['text', 'currency', 'date', 'serial', 'uppercase'].includes(sharedType as string)) && (
        <AccordionSection
          id="typography"
          title="Typography & Text Styling"
          icon={<Type className="w-3.5 h-3.5 text-purple-400" />}
          badge={fontSize ? <span className="text-[10px] text-zinc-400 font-mono">{fontSize}pt</span> : undefined}
          isOpen={openSections.typography}
          onToggle={() => toggleSection('typography')}
        >
          <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium text-zinc-500 mb-1 block">Font Size (pt)</label>
              <input type="number" className="w-full px-3 py-1.5 border border-zinc-700 rounded text-sm bg-zinc-900 text-zinc-200" value={fontSize === 'mixed' ? '' : (fontSize || 14)} placeholder={fontSize === 'mixed' ? 'Mixed' : ''} onChange={e => onUpdateElements({ fontSize: parseInt(e.target.value) || 14 })} />
            </div>
            <div>
              <label className="text-xs font-medium text-zinc-500 mb-1 block">Font Family</label>
              <select className="w-full px-3 py-1.5 border border-zinc-700 rounded text-sm bg-zinc-900 text-zinc-200" value={fontFamily === 'mixed' ? '' : (fontFamily || 'Helvetica')} onChange={e => onUpdateElements({ fontFamily: e.target.value })}>
                {fontFamily === 'mixed' && <option value="">Mixed</option>}
                <option value="Helvetica">Helvetica</option>
                <option value="Arial">Arial</option>
                <option value="'Noto Sans Devanagari', sans-serif">Noto Sans Devanagari (Hindi)</option>
                <option value="Times-Roman">Times Roman</option>
                <option value="Courier">Courier</option>
                <option value="Impact">Impact</option>
                <option value="Georgia">Georgia</option>
                <option value="Verdana">Verdana</option>
                <option value="Trebuchet MS">Trebuchet MS</option>
                <option value="Comic Sans MS">Comic Sans MS</option>
                <option value="monospace">Monospace</option>
              </select>
            </div>
          </div>

          {/* Letter Spacing & Line Height */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[11px] font-medium text-zinc-500 mb-1 block">Letter Spacing (px)</label>
              <input 
                type="number" 
                step="0.5" 
                className="w-full px-2.5 py-1 border border-zinc-700 rounded text-xs bg-zinc-900 text-zinc-200" 
                value={letterSpacing === 'mixed' ? '' : (letterSpacing ?? 0)} 
                placeholder={letterSpacing === 'mixed' ? 'Mixed' : undefined}
                onChange={e => onUpdateElements({ letterSpacing: parseFloat(e.target.value) || 0 })} 
              />
            </div>
            <div>
              <label className="text-[11px] font-medium text-zinc-500 mb-1 block">Line Height Ratio</label>
              <input 
                type="number" 
                step="0.1" 
                min="0.5" 
                max="3.0" 
                className="w-full px-2.5 py-1 border border-zinc-700 rounded text-xs bg-zinc-900 text-zinc-200" 
                value={lineHeight === 'mixed' ? '' : (lineHeight ?? 1.2)} 
                placeholder={lineHeight === 'mixed' ? 'Mixed' : undefined}
                onChange={e => onUpdateElements({ lineHeight: parseFloat(e.target.value) || 1.2 })} 
              />
            </div>
          </div>

          <div className="flex items-center justify-between pt-1">
            <div className="flex gap-1 border border-zinc-700 rounded p-0.5 bg-zinc-900">
              <button title="Align Left" onClick={() => onUpdateElements({ textAlign: 'left' })} className={`flex px-2 py-1 rounded ${textAlign === 'left' ? 'bg-zinc-700' : 'hover:bg-zinc-800'}`}><AlignLeft className="w-4 h-4 text-zinc-300" /></button>
              <button title="Align Center" onClick={() => onUpdateElements({ textAlign: 'center' })} className={`flex px-2 py-1 rounded ${textAlign === 'center' ? 'bg-zinc-700' : 'hover:bg-zinc-800'}`}><AlignCenter className="w-4 h-4 text-zinc-300" /></button>
              <button title="Align Right" onClick={() => onUpdateElements({ textAlign: 'right' })} className={`flex px-2 py-1 rounded ${textAlign === 'right' ? 'bg-zinc-700' : 'hover:bg-zinc-800'}`}><AlignRight className="w-4 h-4 text-zinc-300" /></button>
            </div>
            <div className="flex gap-3">
               <label className="flex items-center gap-1.5 text-sm text-zinc-300 cursor-pointer">
                  <input type="checkbox" checked={isBold === true} ref={input => { if(input) input.indeterminate = isBold === 'mixed'; }} onChange={e => onUpdateElements({ isBold: e.target.checked, fontWeight: e.target.checked ? 'bold' : 'normal' })} />
                  <span className="font-bold">Bold</span>
               </label>
               <label className="flex items-center gap-1.5 text-sm text-zinc-300 cursor-pointer">
                  <input type="checkbox" checked={isItalic === true} ref={input => { if(input) input.indeterminate = isItalic === 'mixed'; }} onChange={e => onUpdateElements({ isItalic: e.target.checked, fontStyle: e.target.checked ? 'italic' : 'normal' })} />
                  <span className="italic">Italic</span>
               </label>
            </div>
          </div>

          
          {/* Advanced Typography */}
          <div className="grid grid-cols-2 gap-3 pt-2">
            <div>
              <label className="text-[11px] font-medium text-zinc-500 mb-1 block">Horiz. Scale (%)</label>
              <input 
                type="number" 
                step="5" 
                className="w-full px-2.5 py-1 border border-zinc-700 rounded text-xs bg-zinc-900 text-zinc-200" 
                value={horizontalScale === 'mixed' ? '' : (horizontalScale ?? 100)} 
                placeholder={horizontalScale === 'mixed' ? 'Mixed' : undefined}
                onChange={e => onUpdateElements({ horizontalScale: parseFloat(e.target.value) || 100 })} 
              />
            </div>
            <div>
              <label className="text-[11px] font-medium text-zinc-500 mb-1 block">Vert. Scale (%)</label>
              <input 
                type="number" 
                step="5" 
                className="w-full px-2.5 py-1 border border-zinc-700 rounded text-xs bg-zinc-900 text-zinc-200" 
                value={verticalScale === 'mixed' ? '' : (verticalScale ?? 100)} 
                placeholder={verticalScale === 'mixed' ? 'Mixed' : undefined}
                onChange={e => onUpdateElements({ verticalScale: parseFloat(e.target.value) || 100 })} 
              />
            </div>
            <div>
              <label className="text-[11px] font-medium text-zinc-500 mb-1 block">Baseline Shift (pt)</label>
              <input 
                type="number" 
                step="1" 
                className="w-full px-2.5 py-1 border border-zinc-700 rounded text-xs bg-zinc-900 text-zinc-200" 
                value={baselineShift === 'mixed' ? '' : (baselineShift ?? 0)} 
                placeholder={baselineShift === 'mixed' ? 'Mixed' : undefined}
                onChange={e => onUpdateElements({ baselineShift: parseFloat(e.target.value) || 0 })} 
              />
            </div>
            <div>
              <label className="text-[11px] font-medium text-zinc-500 mb-1 block">Character Rotation (°)</label>
              <input 
                type="number" 
                step="1" 
                className="w-full px-2.5 py-1 border border-zinc-700 rounded text-xs bg-zinc-900 text-zinc-200" 
                value={textRotation === 'mixed' ? '' : (textRotation ?? 0)} 
                placeholder={textRotation === 'mixed' ? 'Mixed' : undefined}
                onChange={e => onUpdateElements({ textRotation: parseFloat(e.target.value) || 0 })} 
              />
            </div>
          </div>

          {/* Text Decoration & Variant */}
          <div className="flex items-center justify-between pt-1 gap-2 flex-wrap">
            <div className="flex gap-1 border border-zinc-700 rounded p-0.5 bg-zinc-900">
              <button title="Underline" onClick={() => onUpdateElements({ textDecoration: textDecoration === 'underline' ? 'none' : 'underline' })} className={`flex px-2 py-1 rounded ${textDecoration === 'underline' ? 'bg-zinc-700' : 'hover:bg-zinc-800'}`}><Underline className="w-4 h-4 text-zinc-300" /></button>
              <button title="Strikethrough" onClick={() => onUpdateElements({ textDecoration: textDecoration === 'line-through' ? 'none' : 'line-through' })} className={`flex px-2 py-1 rounded ${textDecoration === 'line-through' ? 'bg-zinc-700' : 'hover:bg-zinc-800'}`}><Strikethrough className="w-4 h-4 text-zinc-300" /></button>
            </div>
            <div className="flex gap-1 border border-zinc-700 rounded p-0.5 bg-zinc-900">
              <button title="Superscript" onClick={() => onUpdateElements({ verticalAlign: verticalAlign === 'super' ? 'baseline' : 'super' })} className={`flex px-2 py-1 rounded ${verticalAlign === 'super' ? 'bg-zinc-700' : 'hover:bg-zinc-800'}`}><Superscript className="w-4 h-4 text-zinc-300" /></button>
              <button title="Subscript" onClick={() => onUpdateElements({ verticalAlign: verticalAlign === 'sub' ? 'baseline' : 'sub' })} className={`flex px-2 py-1 rounded ${verticalAlign === 'sub' ? 'bg-zinc-700' : 'hover:bg-zinc-800'}`}><Subscript className="w-4 h-4 text-zinc-300" /></button>
            </div>
            <div className="flex gap-1 border border-zinc-700 rounded p-0.5 bg-zinc-900">
              <button title="Small Caps" onClick={() => onUpdateElements({ textVariant: textVariant === 'small-caps' ? 'normal' : 'small-caps' })} className={`flex px-2 py-1 text-xs font-bold rounded ${textVariant === 'small-caps' ? 'bg-zinc-700 text-white' : 'text-zinc-400 hover:bg-zinc-800'}`}>TT</button>
            </div>
          </div>

          {/* Prefix, Suffix & Text Transform */}
          <div className="p-2.5 bg-zinc-950/60 rounded-lg border border-zinc-800 space-y-2">
            <div className="text-[11px] font-bold text-zinc-400 uppercase tracking-wider">
              Text Decorators & Formatting
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-[10px] text-zinc-500 block mb-0.5">Prefix Text</label>
                <input
                  type="text"
                  placeholder={prefix === 'mixed' ? 'Mixed' : 'e.g. SKU: '}
                  value={prefix === 'mixed' ? '' : (prefix || '')}
                  onChange={e => onUpdateElements({ prefix: e.target.value })}
                  className="w-full px-2 py-1 bg-zinc-900 border border-zinc-700 rounded text-xs text-zinc-200"
                />
              </div>
              <div>
                <label className="text-[10px] text-zinc-500 block mb-0.5">Suffix Text</label>
                <input
                  type="text"
                  placeholder={suffix === 'mixed' ? 'Mixed' : 'e.g.  pcs'}
                  value={suffix === 'mixed' ? '' : (suffix || '')}
                  onChange={e => onUpdateElements({ suffix: e.target.value })}
                  className="w-full px-2 py-1 bg-zinc-900 border border-zinc-700 rounded text-xs text-zinc-200"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-2 pt-1">
              <div>
                <label className="text-[10px] text-zinc-500 block mb-0.5">Text Case</label>
                <select
                  value={textCase === 'mixed' ? '' : (textCase || 'none')}
                  onChange={e => onUpdateElements({ textCase: e.target.value as any })}
                  className="w-full px-2 py-1 bg-zinc-900 border border-zinc-700 rounded text-xs text-zinc-200"
                >
                  {textCase === 'mixed' && <option value="">Mixed</option>}
                  <option value="none">Default / As Is</option>
                  <option value="uppercase">UPPERCASE</option>
                  <option value="lowercase">lowercase</option>
                  <option value="capitalize">Capitalize Words</option>
                </select>
              </div>
              <div className="flex items-end">
                <label className="flex items-center gap-1.5 text-xs text-zinc-300 cursor-pointer pb-1">
                  <input
                    type="checkbox"
                    checked={autoShrink === true}
                    ref={input => { if(input) input.indeterminate = autoShrink === 'mixed'; }}
                    onChange={e => onUpdateElements({ autoShrink: e.target.checked })}
                    className="rounded border-zinc-700 text-blue-600"
                  />
                  <span>Auto-Shrink Text</span>
                </label>
              </div>
            </div>

            {/* Curved Text Control */}
            <div className="pt-2 border-t border-zinc-800/80 space-y-1.5">
              <label className="flex items-center gap-2 text-xs text-indigo-400 font-medium cursor-pointer">
                <input
                  type="checkbox"
                  checked={isCurved === true}
                  ref={input => { if(input) input.indeterminate = isCurved === 'mixed'; }}
                  onChange={e => onUpdateElements({ isCurved: e.target.checked, curveRadius: e.target.checked ? 100 : undefined })}
                  className="rounded border-indigo-700 text-indigo-500"
                />
                <span>Curved Arc Text Effect</span>
              </label>
              {isCurved === true && (
                <div className="flex items-center gap-2 pl-4">
                  <span className="text-[10px] text-zinc-400">Curve Radius:</span>
                  <input
                    type="range"
                    min="30"
                    max="300"
                    value={curveRadius === 'mixed' ? 100 : (curveRadius || 100)}
                    onChange={e => onUpdateElements({ curveRadius: parseFloat(e.target.value) || 100 })}
                    className="w-full accent-indigo-500 cursor-pointer h-1.5 bg-zinc-800 rounded-lg"
                  />
                  <span className="text-xs font-mono font-bold text-indigo-400 w-8 text-right">
                    {curveRadius === 'mixed' ? 'Mix' : (curveRadius || 100)}
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>
      </AccordionSection>
      )}

      
      {/* Date & Currency (if single selected or same type) */}
      {(!isMixedType && sharedType === 'date') && (
        <AccordionSection
          id="dateSettings"
          title="Date Settings & Formatting"
          icon={<Calendar className="w-3.5 h-3.5 text-orange-400" />}
          isOpen={openSections.dateSettings}
          onToggle={() => toggleSection('dateSettings')}
        >
          <div className="space-y-3">
            <button 
              type="button" 
              onClick={() => window.dispatchEvent(new CustomEvent('open-date-picker'))}
              className="w-full px-3 py-2 bg-gradient-to-r from-orange-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white rounded-md text-xs font-bold shadow-xs cursor-pointer"
            >
              Open Calendar Popup
            </button>
            <select 
              value={dateFormat === 'mixed' ? '' : (dateFormat || 'DD/MM/YYYY')}
              onChange={e => {
                const newFmt = e.target.value;
                const currentVal = selectedElements[0]?.staticValue;
                if (currentVal) {
                  const reformatted = formatDateValue(currentVal, newFmt);
                  onUpdateElements({ dateFormat: newFmt, staticValue: reformatted });
                } else {
                  onUpdateElements({ dateFormat: newFmt });
                }
              }}
              className="w-full px-3 py-1.5 bg-zinc-900 border border-zinc-700 rounded-md text-xs text-zinc-200 cursor-pointer"
            >
              {dateFormat === 'mixed' && <option value="">Mixed</option>}
              <option value="DD/MM/YYYY">DD/MM/YYYY (e.g. 26/08/2026)</option>
              <option value="MM/DD/YYYY">MM/DD/YYYY (e.g. 08/26/2026)</option>
              <option value="YYYY-MM-DD">YYYY-MM-DD (e.g. 2026-08-26)</option>
              <option value="DD MMM YYYY">DD MMM YYYY (e.g. 26 Aug 2026)</option>
              <option value="MMM DD, YYYY">MMM DD, YYYY (e.g. Aug 26, 2026)</option>
              <option value="DD MMMM YYYY">DD MMMM YYYY (e.g. 26 August 2026)</option>
              <option value="YYYY/MM/DD">YYYY/MM/DD (e.g. 2026/08/26)</option>
            </select>
          </div>
        </AccordionSection>
      )}
      {(!isMixedType && sharedType === 'currency') && (
        <AccordionSection
          id="currencySettings"
          title="Price Tag & Currency Formatting"
          icon={<DollarSign className="w-3.5 h-3.5 text-emerald-400" />}
          isOpen={openSections.currencySettings}
          onToggle={() => toggleSection('currencySettings')}
        >
          <div className="space-y-3.5 bg-gradient-to-b from-emerald-950/20 to-transparent p-3 rounded-lg border border-emerald-500/20">
            <div className="flex items-center justify-between pb-2 border-b border-zinc-800">
              <div className="flex items-center gap-2">
                <div className="w-6 h-6 rounded bg-emerald-500/20 text-emerald-400 flex items-center justify-center font-bold text-xs shrink-0">
                  $
                </div>
                <div>
                  <h4 className="text-xs font-semibold text-zinc-100 flex items-center gap-1.5">
                    Price Tag & Currency Formatting
                  </h4>
                  <p className="text-[10px] text-zinc-400">Configure price visibility, symbol, decimals & badge style</p>
                </div>
              </div>
              {/* Price Tag Output Visibility Switch */}
              <button
                onClick={() => onUpdateElements({ disabled: !(disabled || isSkipped), isSkipped: !(disabled || isSkipped) })}
                className={`px-2.5 py-1 rounded text-xs font-medium flex items-center gap-1.5 transition-all ${
                  disabled || isSkipped
                    ? 'bg-rose-500/20 text-rose-300 border border-rose-500/40 hover:bg-rose-500/30'
                    : 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/40 hover:bg-emerald-500/30'
                }`}
                title="Toggle whether price tag is printed/exported or hidden"
              >
                {disabled || isSkipped ? (
                  <>
                    <EyeOff className="w-3.5 h-3.5 text-rose-400" />
                    <span>Hidden / Skipped</span>
                  </>
                ) : (
                  <>
                    <Eye className="w-3.5 h-3.5 text-emerald-400" />
                    <span>Visible on Print</span>
                  </>
                )}
              </button>
            </div>

          {/* Quick Currency Symbol Selector Pills */}
          <div>
            <label className="text-xs font-semibold text-zinc-300 mb-1.5 block">Currency Symbol</label>
            <div className="flex flex-wrap items-center gap-1.5 mb-2">
              {['$', '₹', '€', '£', '¥', 'A$', 'C$', 'CHF', 'AED'].map(sym => (
                <button
                  key={sym}
                  type="button"
                  onClick={() => onUpdateElements({ currencySymbol: sym })}
                  className={`min-w-[32px] h-7 px-2 flex items-center justify-center rounded-md text-xs font-bold transition-all shadow-xs cursor-pointer ${
                    (currencySymbol || '₹') === sym
                      ? 'bg-emerald-600 text-white shadow-emerald-900/30 ring-1 ring-emerald-400'
                      : 'bg-zinc-900 text-zinc-300 hover:text-white hover:bg-zinc-800 border border-zinc-700/80'
                  }`}
                  title={`Select currency: ${sym}`}
                >
                  {sym}
                </button>
              ))}
            </div>
          </div>

          {/* Decimals & Thousands Separator */}
          <div className="grid grid-cols-2 gap-2.5">
            <div>
              <label className="text-[11px] font-semibold text-zinc-300 mb-1 block">Decimal Precision</label>
              <div className="grid grid-cols-2 gap-1 bg-zinc-900 p-1 rounded-md border border-zinc-700/80">
                <button
                  type="button"
                  onClick={() => onUpdateElements({ decimalPlaces: 0 })}
                  className={`py-1.5 px-1 rounded text-center text-xs font-semibold transition-colors truncate cursor-pointer ${
                    (decimalPlaces ?? 2) === 0 ? 'bg-emerald-600 text-white shadow-xs' : 'text-zinc-400 hover:text-zinc-200'
                  }`}
                  title="0 decimals e.g. $20"
                >
                  0 ($20)
                </button>
                <button
                  type="button"
                  onClick={() => onUpdateElements({ decimalPlaces: 2 })}
                  className={`py-1.5 px-1 rounded text-center text-xs font-semibold transition-colors truncate cursor-pointer ${
                    (decimalPlaces ?? 2) === 2 ? 'bg-emerald-600 text-white shadow-xs' : 'text-zinc-400 hover:text-zinc-200'
                  }`}
                  title="2 decimals e.g. $19.99"
                >
                  2 ($19.99)
                </button>
              </div>
            </div>

            <div>
              <label className="text-[11px] font-semibold text-zinc-300 mb-1 block">Thousand Separator</label>
              <button
                type="button"
                onClick={() => onUpdateElements({ formatThousands: !(formatThousands ?? true) })}
                className={`w-full py-1.5 px-2.5 rounded-md text-xs font-semibold flex items-center justify-between border transition-colors cursor-pointer h-[34px] ${
                  (formatThousands ?? true)
                    ? 'bg-emerald-950/50 border-emerald-500/60 text-emerald-300 shadow-xs'
                    : 'bg-zinc-900 border-zinc-700 text-zinc-400 hover:text-zinc-300'
                }`}
                title="Format with thousands separator"
              >
                <span>Comma (1,000)</span>
                <span className={`w-2.5 h-2.5 rounded-full ${formatThousands ?? true ? 'bg-emerald-400 shadow-xs shadow-emerald-400/50' : 'bg-zinc-600'}`} />
              </button>
            </div>
          </div>

          {/* Prefix & Suffix Custom Labels */}
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-[11px] font-medium text-zinc-400 block mb-1">Custom Prefix</label>
              <input
                type="text"
                placeholder={prefix === 'mixed' ? 'Mixed' : 'e.g. Price: or MSRP:'}
                value={prefix === 'mixed' ? '' : (prefix || '')}
                onChange={e => onUpdateElements({ prefix: e.target.value })}
                className="w-full px-2.5 py-1.5 bg-zinc-900 border border-zinc-700 rounded-md text-xs text-zinc-200 focus:ring-1 focus:ring-emerald-500 outline-none"
              />
            </div>
            <div>
              <label className="text-[11px] font-medium text-zinc-400 block mb-1">Custom Suffix</label>
              <input
                type="text"
                placeholder={suffix === 'mixed' ? 'Mixed' : 'e.g. /ea or + TAX'}
                value={suffix === 'mixed' ? '' : (suffix || '')}
                onChange={e => onUpdateElements({ suffix: e.target.value })}
                className="w-full px-2.5 py-1.5 bg-zinc-900 border border-zinc-700 rounded-md text-xs text-zinc-200 focus:ring-1 focus:ring-emerald-500 outline-none"
              />
            </div>
          </div>

          {/* Price Badge Visual Presets */}
          <div>
            <label className="text-xs font-semibold text-zinc-300 mb-1.5 block">Price Tag Badge Style</label>
            <div className="grid grid-cols-3 gap-2">
              <button
                type="button"
                onClick={() => onUpdateElements({ backgroundColor: 'transparent', fillColor: 'transparent', borderWidth: 0, color: '#166534' })}
                className="py-1.5 px-2 rounded-md border border-zinc-700 bg-zinc-900 text-xs text-emerald-400 hover:border-emerald-500 font-bold truncate text-center transition-all cursor-pointer"
              >
                Plain Text
              </button>
              <button
                type="button"
                onClick={() => onUpdateElements({ backgroundColor: '#dcfce7', fillColor: '#dcfce7', borderWidth: 1, borderColor: '#166534', color: '#166534', borderRadius: 6 })}
                className="py-1.5 px-2 rounded-md border border-emerald-600 bg-emerald-100 text-xs text-emerald-900 font-bold truncate text-center transition-all cursor-pointer"
              >
                Green Pill
              </button>
              <button
                type="button"
                onClick={() => onUpdateElements({ backgroundColor: '#fee2e2', fillColor: '#fee2e2', borderWidth: 1, borderColor: '#dc2626', color: '#991b1b', borderRadius: 6 })}
                className="py-1.5 px-2 rounded-md border border-red-400 bg-red-100 text-xs text-red-900 font-bold truncate text-center transition-all cursor-pointer"
              >
                Sale Red Tag
              </button>
              <button
                type="button"
                onClick={() => onUpdateElements({ backgroundColor: '#fef9c3', fillColor: '#fef9c3', borderWidth: 1, borderColor: '#ca8a04', color: '#854d0e', borderRadius: 4 })}
                className="py-1.5 px-2 rounded-md border border-yellow-500 bg-yellow-100 text-xs text-yellow-900 font-bold truncate text-center transition-all cursor-pointer"
              >
                Gold Badge
              </button>
              <button
                type="button"
                onClick={() => onUpdateElements({ backgroundColor: '#0f172a', fillColor: '#0f172a', borderWidth: 1, borderColor: '#334155', color: '#ffffff', borderRadius: 8 })}
                className="py-1.5 px-2 rounded-md border border-slate-700 bg-slate-900 text-xs text-white font-bold truncate text-center transition-all cursor-pointer"
              >
                Dark Tag
              </button>
              <button
                type="button"
                onClick={() => onUpdateElements({ backgroundColor: '#2563eb', fillColor: '#2563eb', borderWidth: 0, color: '#ffffff', borderRadius: 20 })}
                className="py-1.5 px-2 rounded-md border border-blue-600 bg-blue-600 text-xs text-white font-bold truncate text-center transition-all cursor-pointer"
              >
                Blue Pill
              </button>
            </div>
          </div>
        </div>
      </AccordionSection>
      )}

      {/* Image Source & Display Mode */}
      {(!isMixedType && sharedType === 'image') && (
        <AccordionSection
          id="imageSettings"
          title="Image Source & Display"
          icon={<ImageIcon className="w-3.5 h-3.5 text-sky-400" />}
          isOpen={openSections.imageSettings}
          onToggle={() => toggleSection('imageSettings')}
        >
          <div className="space-y-3">
            {(imageSrc && imageSrc !== 'mixed') && (
              <div className="w-full h-28 bg-zinc-950 rounded border border-zinc-700 flex items-center justify-center overflow-hidden p-1">
                <img
                  src={imageSrc}
                  alt="Image Preview"
                  className={`max-w-full max-h-full ${
                    selectedElements[0].objectFit === 'cover'
                      ? 'object-cover w-full h-full'
                      : selectedElements[0].objectFit === 'stretch'
                      ? 'object-fill w-full h-full'
                      : 'object-contain'
                  }`}
                />
              </div>
            )}
            <label className="flex items-center justify-center gap-2 px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded cursor-pointer text-xs font-medium transition-colors shadow-sm">
              <ImageIcon className="w-4 h-4" />
              <span>Browse & Upload Image File</span>
              <input 
                type="file" 
                accept="image/*" 
                className="hidden" 
                onChange={e => {
                  const file = e.target.files?.[0];
                  if (file) {
                    const reader = new FileReader();
                    reader.onload = (evt) => {
                      if (evt.target?.result) {
                        const dataUrl = evt.target.result as string;
                        onUpdateElements({ src: dataUrl, staticValue: dataUrl });
                      }
                    };
                    reader.readAsDataURL(file);
                  }
                }} 
              />
            </label>
            <div>
              <label className="text-xs font-medium text-zinc-500 mb-1 block">Or Direct Image URL</label>
              <input 
                type="text"
                className="w-full px-3 py-1.5 bg-zinc-900 border border-zinc-700 rounded-md text-xs text-zinc-200"
                value={selectedElements[0].src || selectedElements[0].staticValue || ''}
                onChange={e => onUpdateElements({ src: e.target.value, staticValue: e.target.value })}
                placeholder="https://..."
              />
            </div>
            <div>
              <label className="text-xs font-medium text-zinc-500 mb-1 block">Image Fit Mode</label>
              <select
                value={selectedElements[0].objectFit || 'contain'}
                onChange={e => onUpdateElements({ objectFit: e.target.value as any })}
                className="w-full px-3 py-1.5 bg-zinc-900 border border-zinc-700 rounded-md text-xs text-zinc-200"
              >
                <option value="contain">Contain (Keep Proportion)</option>
                <option value="cover">Cover (Fill & Crop)</option>
                <option value="stretch">Stretch (Fill Exact Box)</option>
              </select>
            </div>
          </div>
        </AccordionSection>
      )}

      {/* QR & 2D Matrix Settings */}
      {(!isMixedType && ['qr', 'datamatrix', 'aztec', 'pdf417'].includes(sharedType as string)) && (
        <AccordionSection
          id="qrSettings"
          title="2D Code Style & Colors"
          icon={<QrCode className="w-3.5 h-3.5 text-emerald-400" />}
          isOpen={openSections.qrSettings}
          onToggle={() => toggleSection('qrSettings')}
        >
          <div className="space-y-3">
            {/* Quick Contrast Color Presets */}
            <div>
              <label className="text-[11px] font-semibold text-zinc-400 mb-1.5 block">Scanner High-Contrast Presets</label>
              <div className="grid grid-cols-4 gap-1.5">
                {[
                  { name: 'Classic', fg: '#000000', bg: '#ffffff', border: '#e4e4e7' },
                  { name: 'Navy Clean', fg: '#0f172a', bg: '#f8fafc', border: '#cbd5e1' },
                  { name: 'Luxury Gold', fg: '#b45309', bg: '#18181b', border: '#713f12' },
                  { name: 'Forest Green', fg: '#064e3b', bg: '#ecfdf5', border: '#a7f3d0' }
                ].map(preset => (
                  <button
                    key={preset.name}
                    type="button"
                    onClick={() => onUpdateElements({ color: preset.fg, backgroundColor: preset.bg, isTransparentBg: false })}
                    className="p-1.5 rounded border border-zinc-800 bg-zinc-950 hover:border-zinc-600 flex flex-col items-center gap-1 transition-all cursor-pointer group"
                    title={`Apply ${preset.name} preset`}
                  >
                    <div className="flex items-center gap-0.5">
                      <div className="w-3.5 h-3.5 rounded-full border" style={{ backgroundColor: preset.fg, borderColor: preset.border }} />
                      <div className="w-3.5 h-3.5 rounded-full border" style={{ backgroundColor: preset.bg, borderColor: preset.border }} />
                    </div>
                    <span className="text-[9px] text-zinc-400 group-hover:text-zinc-200 truncate w-full text-center">{preset.name}</span>
                  </button>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-medium text-zinc-500 mb-1 block">Module Fill Color</label>
                <div className="flex items-center gap-2">
                  <input 
                    type="color" 
                    value={selectedElements[0].color || '#000000'} 
                    onChange={e => onUpdateElements({ color: e.target.value })} 
                    className="w-7 h-7 rounded border border-zinc-700 bg-transparent cursor-pointer" 
                  />
                  <input 
                    type="text" 
                    value={selectedElements[0].color || '#000000'} 
                    onChange={e => onUpdateElements({ color: e.target.value })} 
                    className="w-20 px-2 py-1 bg-zinc-900 border border-zinc-700 rounded text-xs text-zinc-200 uppercase font-mono" 
                  />
                </div>
              </div>

              <div>
                <label className="text-xs font-medium text-zinc-500 mb-1 block">Background</label>
                <div className="space-y-1.5">
                  <label className="flex items-center gap-2 text-xs text-zinc-300 cursor-pointer">
                    <input 
                      type="checkbox" 
                      checked={selectedElements[0].isTransparentBg || false} 
                      onChange={e => onUpdateElements({ isTransparentBg: e.target.checked, backgroundColor: e.target.checked ? 'transparent' : '#ffffff' })} 
                      className="rounded border-zinc-700 text-blue-600 focus:ring-0" 
                    />
                    <span>Transparent</span>
                  </label>
                  {!selectedElements[0].isTransparentBg && (
                    <div className="flex items-center gap-2">
                      <input 
                        type="color" 
                        value={selectedElements[0].backgroundColor || '#ffffff'} 
                        onChange={e => onUpdateElements({ backgroundColor: e.target.value })} 
                        className="w-7 h-7 rounded border border-zinc-700 bg-transparent cursor-pointer" 
                      />
                      <input 
                        type="text" 
                        value={selectedElements[0].backgroundColor || '#ffffff'} 
                        onChange={e => onUpdateElements({ backgroundColor: e.target.value })} 
                        className="w-20 px-2 py-1 bg-zinc-900 border border-zinc-700 rounded text-xs text-zinc-200 uppercase font-mono" 
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-medium text-zinc-500 mb-1 block">Error Correction</label>
                <select value={qrErrorCorrection === 'mixed' ? '' : (qrErrorCorrection || 'M')} onChange={e => onUpdateElements({ qrErrorCorrection: e.target.value as any })} className="w-full px-2 py-1.5 border border-zinc-700 rounded text-xs bg-zinc-900 text-zinc-200">
                  {qrErrorCorrection === 'mixed' && <option value="">Mixed</option>}
                  <option value="L">Low (7%)</option>
                  <option value="M">Medium (15%)</option>
                  <option value="Q">Quartile (25%)</option>
                  <option value="H">High (30%)</option>
                </select>
              </div>
              <div>
                <label className="text-xs font-medium text-zinc-500 mb-1 block">Module Shape</label>
                <select value={qrStyle === 'mixed' ? '' : (qrStyle || 'square')} onChange={e => onUpdateElements({ qrStyle: e.target.value as any })} className="w-full px-2 py-1.5 border border-zinc-700 rounded text-xs bg-zinc-900 text-zinc-200">
                  {qrStyle === 'mixed' && <option value="">Mixed</option>}
                  <option value="square">Standard Squares</option>
                  <option value="dots">Dots</option>
                  <option value="fluid">Fluid / Rounded</option>
                </select>
              </div>
            </div>
          </div>
        </AccordionSection>
      )}
      
      {/* Barcode Settings */}
      {(!isMixedType && ['barcode', 'ean13', 'upca', 'datamatrix', 'qr', 'aztec', 'pdf417'].includes(sharedType as string)) && (
        <AccordionSection
          id="barcodeSettings"
          title="Barcode Symbology & Text"
          icon={<BarcodeIcon className="w-3.5 h-3.5 text-teal-400" />}
          isOpen={openSections.barcodeSettings}
          onToggle={() => toggleSection('barcodeSettings')}
        >
          <div className="space-y-3">
            <div>
              <label className="text-xs font-medium text-zinc-500 mb-1 block">Symbology / Format</label>
              <select
                value={selectedElements[0].type || 'barcode'}
                onChange={e => {
                  const newType = e.target.value;
                  let defaultVal = selectedElements[0].staticValue || selectedElements[0].text || '123456789012';
                  if (newType === 'ean13' && defaultVal.length !== 13) {
                    defaultVal = '5901234123457';
                  } else if (newType === 'upca' && defaultVal.length !== 12) {
                    defaultVal = '012345678905';
                  } else if (newType === 'aztec' && defaultVal.length < 4) {
                    defaultVal = 'AZTEC2026';
                  } else if (newType === 'pdf417' && defaultVal.length < 4) {
                    defaultVal = 'PDF417-BATCH';
                  }
                  onUpdateElements({ type: newType as any, staticValue: defaultVal, text: defaultVal });
                }}
                className="w-full px-3 py-1.5 border border-zinc-700 rounded text-xs bg-zinc-900 text-zinc-200 font-medium"
              >
                <option value="ean13">EAN-13 Barcode</option>
                <option value="upca">UPC-A Barcode</option>
                <option value="datamatrix">DataMatrix (2D Code)</option>
                <option value="aztec">Aztec Code (2D Matrix)</option>
                <option value="pdf417">PDF417 (Stacked 2D Barcode)</option>
                <option value="barcode">Code 128 / Standard Barcode</option>
                <option value="qr">QR Code</option>
              </select>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-medium text-zinc-500 mb-1 block">Bars & Text Color</label>
                <div className="flex items-center gap-2">
                  <input 
                    type="color" 
                    value={selectedElements[0].color || '#000000'} 
                    onChange={e => onUpdateElements({ color: e.target.value })} 
                    className="w-7 h-7 rounded border border-zinc-700 bg-transparent cursor-pointer" 
                  />
                  <input 
                    type="text" 
                    value={selectedElements[0].color || '#000000'} 
                    onChange={e => onUpdateElements({ color: e.target.value })} 
                    className="w-20 px-2 py-1 bg-zinc-900 border border-zinc-700 rounded text-xs text-zinc-200 uppercase font-mono" 
                  />
                </div>
              </div>

              <div>
                <label className="text-xs font-medium text-zinc-500 mb-1 block">Background</label>
                <div className="space-y-1.5">
                  <label className="flex items-center gap-2 text-xs text-zinc-300 cursor-pointer">
                    <input 
                      type="checkbox" 
                      checked={selectedElements[0].isTransparentBg || false} 
                      onChange={e => onUpdateElements({ isTransparentBg: e.target.checked, backgroundColor: e.target.checked ? 'transparent' : '#ffffff' })} 
                      className="rounded border-zinc-700 text-blue-600 focus:ring-0" 
                    />
                    <span>Transparent</span>
                  </label>
                  {!selectedElements[0].isTransparentBg && (
                    <div className="flex items-center gap-2">
                      <input 
                        type="color" 
                        value={selectedElements[0].backgroundColor || '#ffffff'} 
                        onChange={e => onUpdateElements({ backgroundColor: e.target.value })} 
                        className="w-7 h-7 rounded border border-zinc-700 bg-transparent cursor-pointer" 
                      />
                      <input 
                        type="text" 
                        value={selectedElements[0].backgroundColor || '#ffffff'} 
                        onChange={e => onUpdateElements({ backgroundColor: e.target.value })} 
                        className="w-20 px-2 py-1 bg-zinc-900 border border-zinc-700 rounded text-xs text-zinc-200 uppercase font-mono" 
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>

            {['barcode', 'ean13', 'upca'].includes(selectedElements[0].type) && (
              <label className="flex items-center gap-2 text-xs text-zinc-300 cursor-pointer pt-1">
                <input type="checkbox" checked={showBarcodeText === true} ref={input => { if(input) input.indeterminate = showBarcodeText === 'mixed'; }} onChange={e => onUpdateElements({ showBarcodeText: e.target.checked })} className="rounded border-zinc-700" />
                <span>Show Readable Text</span>
              </label>
            )}
          </div>
        </AccordionSection>
      )}

      {/* Shape, Line & Vector Path Geometry */}
      {(!isMixedType && ['line', 'path', 'pen', 'star', 'polygon'].includes(sharedType as string)) && (
        <AccordionSection
          id="shapeSettings"
          title="Shape & Vector Path Geometry"
          icon={<Layers className="w-3.5 h-3.5 text-orange-400" />}
          isOpen={openSections.shapeSettings}
          onToggle={() => toggleSection('shapeSettings')}
        >
          <div className="space-y-3">
            {/* Line Effects & Properties */}
            {sharedType === 'line' && (
              <div className="space-y-3">
                <label className="text-xs font-semibold text-zinc-300 block">Line Effect Options</label>
          <div className="space-y-3">
            <div>
              <label className="text-xs font-medium text-zinc-500 mb-1 block">Effect / Warp Preset</label>
              <select 
                value={lineEffect === 'mixed' ? '' : (lineEffect || 'none')} 
                onChange={e => onUpdateElements({ lineEffect: e.target.value as any })}
                className="w-full px-3 py-1.5 border border-zinc-700 rounded text-xs bg-zinc-900 text-zinc-200 font-medium"
              >
                {lineEffect === 'mixed' && <option value="">Mixed</option>}
                <option value="none">Straight Line</option>
                <option value="arc">Arc Bend</option>
                <option value="arc_lower">Arc Lower</option>
                <option value="arc_upper">Arc Upper</option>
                <option value="arch">Arch</option>
                <option value="bulge">Bulge</option>
                <option value="shell_lower">Shell Lower</option>
                <option value="shell_upper">Shell Upper</option>
                <option value="flag">Flag Wave</option>
                <option value="wave">Sine Wave</option>
                <option value="fish">Fish Curve</option>
                <option value="rise">Rise Bend</option>
                <option value="fisheye">Fisheye</option>
                <option value="inflate">Inflate</option>
                <option value="squeeze">Squeeze</option>
                <option value="twist">Twist / Loop</option>
                <option value="zigzag">Zig Zag</option>
              </select>
            </div>

            {lineEffect && lineEffect !== 'none' && lineEffect !== 'mixed' && (
              <div className="p-3 bg-zinc-950/80 rounded-lg border border-zinc-800 space-y-3">
                <div className="text-[11px] font-bold text-zinc-400 uppercase tracking-wider border-b border-zinc-800 pb-1">
                  Effect / Warp Controls
                </div>

                {/* Size / Amplitude (pt) */}
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <label className="text-xs font-medium text-zinc-300">Bend / Amplitude:</label>
                    <span className="text-xs font-mono font-bold text-blue-400">
                      {effectAmplitude === 'mixed' ? 'Mix' : (effectAmplitude ?? 8)} pt
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <input 
                      type="range" 
                      min="1" 
                      max="40" 
                      value={effectAmplitude === 'mixed' ? 8 : (effectAmplitude ?? 8)} 
                      onChange={e => onUpdateElements({ effectAmplitude: parseFloat(e.target.value) || 8 })}
                      className="w-full accent-blue-500 cursor-pointer h-1.5 bg-zinc-800 rounded-lg" 
                    />
                    <input 
                      type="number" 
                      min="1" 
                      max="100" 
                      value={effectAmplitude === 'mixed' ? '' : (effectAmplitude ?? 8)} 
                      placeholder={effectAmplitude === 'mixed' ? 'Mix' : undefined}
                      onChange={e => onUpdateElements({ effectAmplitude: parseFloat(e.target.value) || 8 })}
                      className="w-14 px-1.5 py-1 bg-zinc-900 border border-zinc-700 rounded text-xs text-center text-zinc-200 font-mono" 
                    />
                  </div>
                </div>

                {/* Ridges per segment / Frequency */}
                {['zigzag', 'wave', 'sine', 'twist', 'loop'].includes(lineEffect as string) && (
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <label className="text-xs font-medium text-zinc-300">Ridges / Segments:</label>
                      <span className="text-xs font-mono font-bold text-indigo-400">
                        {effectFrequency === 'mixed' ? 'Mix' : (effectFrequency ?? 8)}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <input 
                        type="range" 
                        min="1" 
                        max="30" 
                        value={effectFrequency === 'mixed' ? 8 : (effectFrequency ?? 8)} 
                        onChange={e => onUpdateElements({ effectFrequency: parseInt(e.target.value) || 8 })}
                        className="w-full accent-indigo-500 cursor-pointer h-1.5 bg-zinc-800 rounded-lg" 
                      />
                      <input 
                        type="number" 
                        min="1" 
                        max="50" 
                        value={effectFrequency === 'mixed' ? '' : (effectFrequency ?? 8)} 
                        placeholder={effectFrequency === 'mixed' ? 'Mix' : undefined}
                        onChange={e => onUpdateElements({ effectFrequency: parseInt(e.target.value) || 8 })}
                        className="w-14 px-1.5 py-1 bg-zinc-900 border border-zinc-700 rounded text-xs text-center text-zinc-200 font-mono" 
                      />
                    </div>
                  </div>
                )}

                {/* Points Style: Smooth vs Corner */}
                {['zigzag', 'wave', 'sine'].includes(lineEffect as string) && (
                  <div>
                    <label className="text-xs font-medium text-zinc-300 block mb-1">Points:</label>
                    <div className="grid grid-cols-2 gap-2 bg-zinc-900 p-1 rounded-md border border-zinc-800">
                      <button
                        type="button"
                        onClick={() => onUpdateElements({ effectPointsStyle: 'corner' })}
                        className={`py-1 text-xs font-medium rounded transition-colors ${
                          (effectPointsStyle || 'corner') === 'corner' 
                            ? 'bg-blue-600 text-white font-bold' 
                            : 'text-zinc-400 hover:text-zinc-200'
                        }`}
                      >
                        Corner (Sharp)
                      </button>
                      <button
                        type="button"
                        onClick={() => onUpdateElements({ effectPointsStyle: 'smooth' })}
                        className={`py-1 text-xs font-medium rounded transition-colors ${
                          effectPointsStyle === 'smooth' 
                            ? 'bg-blue-600 text-white font-bold' 
                            : 'text-zinc-400 hover:text-zinc-200'
                        }`}
                      >
                        Smooth (Curved)
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Advanced Stroke Properties (Illustrator style) */}
      {(!isMixedType && ['line', 'path', 'pen', 'star', 'polygon'].includes(sharedType as string)) && (
        <div className="pt-3 border-t border-zinc-800 space-y-3">
          <div className="flex items-center justify-between">
            <label className="text-xs font-semibold text-zinc-300">Stroke Options</label>
          </div>
          <div className="space-y-3 bg-zinc-950/80 p-3 rounded-lg border border-zinc-800">
            {/* Cap & Corner */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-[10px] uppercase font-bold text-zinc-500 mb-1.5 block tracking-wider">Cap</label>
                <div className="flex bg-zinc-900 rounded border border-zinc-700 overflow-hidden">
                  <button type="button" onClick={() => onUpdateElements({ lineCap: 'butt' })} className={`flex-1 py-1.5 flex justify-center ${lineCap === 'butt' ? 'bg-zinc-700 text-white' : 'text-zinc-400 hover:bg-zinc-800'}`} title="Butt Cap">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="4" strokeLinecap="butt"><line x1="4" y1="12" x2="20" y2="12"/></svg>
                  </button>
                  <button type="button" onClick={() => onUpdateElements({ lineCap: 'round' })} className={`flex-1 py-1.5 flex justify-center border-l border-zinc-700 ${(!lineCap || lineCap === 'round') ? 'bg-zinc-700 text-white' : 'text-zinc-400 hover:bg-zinc-800'}`} title="Round Cap">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="4" strokeLinecap="round"><line x1="6" y1="12" x2="18" y2="12"/></svg>
                  </button>
                  <button type="button" onClick={() => onUpdateElements({ lineCap: 'square' })} className={`flex-1 py-1.5 flex justify-center border-l border-zinc-700 ${lineCap === 'square' ? 'bg-zinc-700 text-white' : 'text-zinc-400 hover:bg-zinc-800'}`} title="Projecting Cap">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="4" strokeLinecap="square"><line x1="6" y1="12" x2="18" y2="12"/></svg>
                  </button>
                </div>
              </div>
              <div>
                <label className="text-[10px] uppercase font-bold text-zinc-500 mb-1.5 block tracking-wider">Corner</label>
                <div className="flex bg-zinc-900 rounded border border-zinc-700 overflow-hidden">
                  <button type="button" onClick={() => onUpdateElements({ lineJoin: 'miter' })} className={`flex-1 py-1.5 flex justify-center ${(!lineJoin || lineJoin === 'miter') ? 'bg-zinc-700 text-white' : 'text-zinc-400 hover:bg-zinc-800'}`} title="Miter Join">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinejoin="miter"><path d="M 4 20 L 12 4 L 20 20"/></svg>
                  </button>
                  <button type="button" onClick={() => onUpdateElements({ lineJoin: 'round' })} className={`flex-1 py-1.5 flex justify-center border-l border-zinc-700 ${lineJoin === 'round' ? 'bg-zinc-700 text-white' : 'text-zinc-400 hover:bg-zinc-800'}`} title="Round Join">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinejoin="round"><path d="M 4 20 L 12 4 L 20 20"/></svg>
                  </button>
                  <button type="button" onClick={() => onUpdateElements({ lineJoin: 'bevel' })} className={`flex-1 py-1.5 flex justify-center border-l border-zinc-700 ${lineJoin === 'bevel' ? 'bg-zinc-700 text-white' : 'text-zinc-400 hover:bg-zinc-800'}`} title="Bevel Join">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinejoin="bevel"><path d="M 4 20 L 12 4 L 20 20"/></svg>
                  </button>
                </div>
              </div>
            </div>

            {/* Dashed Line */}
            <div className="pt-2 border-t border-zinc-800">
              <label className="flex items-center gap-2 cursor-pointer mb-2">
                <input 
                  type="checkbox" 
                  className="rounded border-zinc-700" 
                  checked={!!lineDash && lineDash !== 'none' && lineDash !== 'solid'} 
                  ref={input => { if(input) input.indeterminate = lineDash === 'mixed'; }}
                  onChange={(e) => onUpdateElements({ lineDash: e.target.checked ? 'dashed' : 'solid' })} 
                />
                <span className="text-xs text-zinc-300 font-medium">Dashed Line</span>
              </label>
              {(lineDash && lineDash !== 'none' && lineDash !== 'solid' && lineDash !== 'mixed') && (
                <div className="pl-6">
                  <input 
                    type="text" 
                    placeholder="e.g. 5, 2, 1, 2"
                    value={['dashed', 'dotted'].includes(lineDash) ? '' : lineDash}
                    onChange={(e) => onUpdateElements({ lineDash: e.target.value || 'dashed' })}
                    className="w-full px-2 py-1 bg-zinc-900 border border-zinc-700 rounded text-xs text-zinc-200"
                  />
                  <div className="text-[10px] text-zinc-500 mt-1">Enter dash/gap array (e.g. "12 4")</div>
                </div>
              )}
            </div>

            {/* Arrowheads (only for open paths) */}
            {['line', 'path', 'pen'].includes(sharedType as string) && (
              <div className="pt-2 border-t border-zinc-800">
                <label className="text-[10px] uppercase font-bold text-zinc-500 mb-1.5 block tracking-wider">Arrowheads</label>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-[10px] text-zinc-400 mb-1 block">Start</label>
                    <select 
                      value={arrowheadStart === 'mixed' ? '' : (arrowheadStart || 'none')}
                      onChange={(e) => onUpdateElements({ arrowheadStart: e.target.value as any })}
                      className="w-full px-2 py-1 bg-zinc-900 border border-zinc-700 rounded text-xs text-zinc-200"
                    >
                      {arrowheadStart === 'mixed' && <option value="">Mixed</option>}
                      <option value="none">None</option>
                      <option value="arrow">Arrow</option>
                      <option value="circle">Circle</option>
                      <option value="square">Square</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-[10px] text-zinc-400 mb-1 block">End</label>
                    <select 
                      value={arrowheadEnd === 'mixed' ? '' : (arrowheadEnd || 'none')}
                      onChange={(e) => onUpdateElements({ arrowheadEnd: e.target.value as any })}
                      className="w-full px-2 py-1 bg-zinc-900 border border-zinc-700 rounded text-xs text-zinc-200"
                    >
                      {arrowheadEnd === 'mixed' && <option value="">Mixed</option>}
                      <option value="none">None</option>
                      <option value="arrow">Arrow</option>
                      <option value="circle">Circle</option>
                      <option value="square">Square</option>
                    </select>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Star Shape Properties */}
      {(!isMixedType && sharedType === 'star') && (
        <div className="pt-3 border-t border-zinc-800 space-y-3">
          <label className="text-xs font-semibold text-zinc-300 block">Star Properties</label>
          <div className="grid grid-cols-3 gap-2">
            <div>
              <label className="text-xs font-medium text-zinc-500 mb-1 block">Points</label>
              <input 
                type="number" 
                min="3" 
                max="12" 
                value={starPoints === 'mixed' ? '' : (starPoints || 5)} 
                placeholder={starPoints === 'mixed' ? 'Mix' : undefined}
                onChange={e => onUpdateElements({ starPoints: parseInt(e.target.value) || 5 })}
                className="w-full px-2 py-1.5 border border-zinc-700 rounded text-xs bg-zinc-900 text-zinc-200"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-zinc-500 mb-1 block">Inner Ratio</label>
              <input 
                type="number" 
                step="0.05" 
                min="0.1" 
                max="0.9" 
                value={starInnerRadiusRatio === 'mixed' ? '' : (starInnerRadiusRatio || 0.4)} 
                placeholder={starInnerRadiusRatio === 'mixed' ? 'Mix' : undefined}
                onChange={e => onUpdateElements({ starInnerRadiusRatio: parseFloat(e.target.value) || 0.4 })}
                className="w-full px-2 py-1.5 border border-zinc-700 rounded text-xs bg-zinc-900 text-zinc-200"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-zinc-500 mb-1 block">Corner Radius</label>
              <input 
                type="number" 
                min="0" 
                max="50" 
                value={borderRadius === 'mixed' ? '' : (borderRadius ?? 0)} 
                placeholder={borderRadius === 'mixed' ? 'Mix' : undefined}
                onChange={e => onUpdateElements({ cornerRadius: parseFloat(e.target.value) || 0, borderRadius: parseFloat(e.target.value) || 0 })}
                className="w-full px-2 py-1.5 border border-zinc-700 rounded text-xs bg-zinc-900 text-zinc-200"
              />
            </div>
          </div>
        </div>
      )}

      {/* Polygon Shape Properties */}
      {(!isMixedType && sharedType === 'polygon') && (
        <div className="pt-3 border-t border-zinc-800 space-y-3">
          <label className="text-xs font-semibold text-zinc-300 block">Polygon Properties</label>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium text-zinc-500 mb-1 block">Sides Count</label>
              <input 
                type="number" 
                min="3" 
                max="12" 
                value={polygonSides === 'mixed' ? '' : (polygonSides || 6)} 
                placeholder={polygonSides === 'mixed' ? 'Mix' : undefined}
                onChange={e => onUpdateElements({ polygonSides: parseInt(e.target.value) || 6 })}
                className="w-full px-3 py-1.5 border border-zinc-700 rounded text-xs bg-zinc-900 text-zinc-200"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-zinc-500 mb-1 block">Corner Radius</label>
              <input 
                type="number" 
                min="0" 
                max="50" 
                value={borderRadius === 'mixed' ? '' : (borderRadius ?? 0)} 
                placeholder={borderRadius === 'mixed' ? 'Mix' : undefined}
                onChange={e => onUpdateElements({ cornerRadius: parseFloat(e.target.value) || 0, borderRadius: parseFloat(e.target.value) || 0 })}
                className="w-full px-3 py-1.5 border border-zinc-700 rounded text-xs bg-zinc-900 text-zinc-200"
              />
            </div>
          </div>
        </div>
      )}

      {/* Vector Path / Pen Properties */}
      {(!isMixedType && ['path', 'pen'].includes(sharedType as string)) && (
        <div className="pt-3 border-t border-zinc-800 space-y-3">
          <label className="text-xs font-semibold text-zinc-300 block">Vector Path Properties</label>
          <div className="flex items-center gap-4">
            <label className="flex items-center gap-2 text-xs text-zinc-300 cursor-pointer">
              <input 
                type="checkbox" 
                checked={closedPath === true} 
                ref={input => { if (input) input.indeterminate = closedPath === 'mixed'; }}
                onChange={e => onUpdateElements({ closedPath: e.target.checked })}
              />
              <span>Closed Path</span>
            </label>
            <label className="flex items-center gap-2 text-xs text-zinc-300 cursor-pointer">
              <input 
                type="checkbox" 
                checked={isSmooth === true} 
                ref={input => { if (input) input.indeterminate = isSmooth === 'mixed'; }}
                onChange={e => onUpdateElements({ isSmooth: e.target.checked })}
              />
              <span>Smooth Curves</span>
            </label>
          </div>
        </div>
      )}
          </div>
        </AccordionSection>
      )}

      {/* Serial Number Properties */}
      {(!isMixedType && sharedType === 'serial') && (
        <AccordionSection
          id="dynamicSettings"
          title="Serial Number & Increment Settings"
          icon={<SlidersHorizontal className="w-3.5 h-3.5 text-green-400" />}
          isOpen={openSections.dynamicSettings}
          onToggle={() => toggleSection('dynamicSettings')}
        >
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-xs font-medium text-zinc-500 mb-1 block">Prefix</label>
                <input 
                  type="text" 
                  value={serialPrefix === 'mixed' ? '' : (serialPrefix || 'SN-')} 
                  placeholder={serialPrefix === 'mixed' ? 'Mixed' : undefined}
                  onChange={e => onUpdateElements({ serialPrefix: e.target.value })}
                  className="w-full px-2 py-1.5 border border-zinc-700 rounded text-xs bg-zinc-900 text-zinc-200"
                />
              </div>
              <div>
                <label className="text-xs font-medium text-zinc-500 mb-1 block">Suffix</label>
                <input 
                  type="text" 
                  placeholder={suffix === 'mixed' ? 'Mixed' : 'e.g. -A'}
                  value={suffix === 'mixed' ? '' : (suffix || '')} 
                  onChange={e => onUpdateElements({ suffix: e.target.value })}
                  className="w-full px-2 py-1.5 border border-zinc-700 rounded text-xs bg-zinc-900 text-zinc-200"
                />
              </div>
            </div>
            <div className="grid grid-cols-3 gap-2">
              <div>
                <label className="text-xs font-medium text-zinc-500 mb-1 block">Start #</label>
                <input 
                  type="number" 
                  value={serialStart === 'mixed' ? '' : (serialStart || 1)} 
                  placeholder={serialStart === 'mixed' ? 'Mix' : undefined}
                  onChange={e => onUpdateElements({ serialStart: parseInt(e.target.value) || 1 })}
                  className="w-full px-2 py-1.5 border border-zinc-700 rounded text-xs bg-zinc-900 text-zinc-200"
                />
              </div>
              <div>
                <label className="text-xs font-medium text-zinc-500 mb-1 block">Padding Digits</label>
                <input 
                  type="number" 
                  min="1" 
                  max="10" 
                  value={serialPadding === 'mixed' ? '' : (serialPadding || 4)} 
                  placeholder={serialPadding === 'mixed' ? 'Mix' : undefined}
                  onChange={e => onUpdateElements({ serialPadding: parseInt(e.target.value) || 4 })}
                  className="w-full px-2 py-1.5 border border-zinc-700 rounded text-xs bg-zinc-900 text-zinc-200"
                />
              </div>
              <div>
                <label className="text-xs font-medium text-zinc-500 mb-1 block">Step (+N)</label>
                <input 
                  type="number" 
                  min="1" 
                  value={serialStep === 'mixed' ? '' : (serialStep || 1)} 
                  placeholder={serialStep === 'mixed' ? 'Mix' : undefined}
                  onChange={e => onUpdateElements({ serialStep: parseInt(e.target.value) || 1 })}
                  className="w-full px-2 py-1.5 border border-zinc-700 rounded text-xs bg-zinc-900 text-zinc-200"
                />
              </div>
            </div>
          </div>
        </AccordionSection>
      )}

      {/* Quick Export Panel Section */}
      <AccordionSection
        id="export"
        title="Quick Export (PNG / SVG)"
        icon={<Download className="w-3.5 h-3.5 text-emerald-400" />}
        badge={
          <span className="text-[10px] text-emerald-400 font-mono">
            {selectedElements.length === 1 ? '1 item' : `${selectedElements.length} items`}
          </span>
        }
        isOpen={openSections.export}
        onToggle={() => toggleSection('export')}
      >
        <QuickExportSection 
          selectedElements={selectedElements} 
          csvData={csvData} 
          previewRecordIndex={previewRecordIndex} 
        />
      </AccordionSection>
    </div>
  );

  if (isModalMode) {
    return (
      <div className="space-y-3">
        {/* Top Section Expand/Collapse Controls */}
        <div className="flex items-center justify-between px-3 py-2 text-xs bg-zinc-900/80 border border-zinc-800 rounded-lg">
          <span className="text-xs font-medium text-zinc-300 flex items-center gap-1.5">
            <SlidersHorizontal className="w-3.5 h-3.5 text-blue-400" />
            <span>Properties Sections</span>
          </span>
          <div className="flex items-center gap-1.5">
            <button
              type="button"
              onClick={expandAllSections}
              className="text-[10px] font-medium px-2 py-0.5 rounded bg-zinc-800 hover:bg-zinc-700 text-zinc-300 hover:text-white transition-colors border border-zinc-700/60 cursor-pointer shadow-xs active:scale-95"
            >
              Expand All
            </button>
            <button
              type="button"
              onClick={collapseAllSections}
              className="text-[10px] font-medium px-2 py-0.5 rounded bg-zinc-800 hover:bg-zinc-700 text-zinc-300 hover:text-white transition-colors border border-zinc-700/60 cursor-pointer shadow-xs active:scale-95"
            >
              Collapse All
            </button>
          </div>
        </div>

        <motion.div 
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={macSpring}
          className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-start text-zinc-100"
        >
          <div className="bg-zinc-900/45 border border-zinc-800/80 p-4 rounded-xl space-y-4 shadow-md backdrop-blur-xs">
            <h3 className="text-xs font-bold text-blue-400 uppercase tracking-wider flex items-center gap-1.5 pb-2 border-b border-zinc-800">
              <LayoutGrid className="w-4 h-4 text-blue-500 animate-pulse" /> Alignment & Layout
            </h3>
            <div className="space-y-4">
              {renderColumn1()}
            </div>
          </div>

          <div className="bg-zinc-900/45 border border-zinc-800/80 p-4 rounded-xl space-y-4 shadow-md backdrop-blur-xs">
            <h3 className="text-xs font-bold text-indigo-400 uppercase tracking-wider flex items-center gap-1.5 pb-2 border-b border-zinc-800">
              <span className="text-indigo-400 font-bold">🎨</span> Fills, Strokes & Finishes
            </h3>
            <div className="space-y-4">
              {renderColumn2()}
            </div>
          </div>

          <div className="bg-zinc-900/45 border border-zinc-800/80 p-4 rounded-xl space-y-4 shadow-md backdrop-blur-xs">
            <h3 className="text-xs font-bold text-amber-400 uppercase tracking-wider flex items-center gap-1.5 pb-2 border-b border-zinc-800">
              <span className="text-amber-400 font-bold">⚙️</span> Settings & Export
            </h3>
            <div className="space-y-4 max-h-[70vh] overflow-y-auto pr-1 custom-scrollbar animate-in fade-in duration-200">
              {renderColumn3()}
            </div>
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <motion.div 
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={macSpring}
      className="flex flex-col gap-3"
    >
      {/* Top Section Expand/Collapse Controls */}
      <div className="flex items-center justify-between px-1 py-1 text-xs border-b border-zinc-800/80 mb-1">
        <span className="text-[11px] font-medium text-zinc-400 flex items-center gap-1.5">
          <SlidersHorizontal className="w-3 h-3 text-blue-400" />
          <span>Properties Accordion</span>
        </span>
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={expandAllSections}
            className="text-[10px] font-medium px-2 py-0.5 rounded bg-zinc-800 hover:bg-zinc-700 text-zinc-300 hover:text-white transition-colors border border-zinc-700/60 cursor-pointer shadow-xs active:scale-95"
          >
            Expand All
          </button>
          <button
            type="button"
            onClick={collapseAllSections}
            className="text-[10px] font-medium px-2 py-0.5 rounded bg-zinc-800 hover:bg-zinc-700 text-zinc-300 hover:text-white transition-colors border border-zinc-700/60 cursor-pointer shadow-xs active:scale-95"
          >
            Collapse All
          </button>
        </div>
      </div>
      {renderColumn1()}
      {renderColumn2()}
      {renderColumn3()}
    </motion.div>
  );
});

const QuickExportSection: React.FC<{
  selectedElements: VdpElement[];
  csvData: CSVData | null;
  previewRecordIndex?: number;
}> = ({ selectedElements, csvData, previewRecordIndex = 0 }) => {
  const [targetElementId, setTargetElementId] = useState<string>('all');
  const [isTransparentBg, setIsTransparentBg] = useState<boolean>(true);
  const [scale, setScale] = useState<number>(4);
  const [isExportingPng, setIsExportingPng] = useState<boolean>(false);
  const [isExportingSvg, setIsExportingSvg] = useState<boolean>(false);
  const [copiedSvg, setCopiedSvg] = useState<boolean>(false);

  // Active elements for export calculation
  const exportTargetElements = targetElementId === 'all'
    ? selectedElements
    : selectedElements.filter(el => el.id === targetElementId);

  const { width, height } = getElementsBoundingBox(exportTargetElements.length > 0 ? exportTargetElements : selectedElements);

  const handleExportPng = async () => {
    setIsExportingPng(true);
    try {
      await exportElementsAsPng(selectedElements, {
        targetElementId: targetElementId === 'all' ? undefined : targetElementId,
        isTransparentBg,
        scale,
        csvData,
        recordIndex: previewRecordIndex
      });
    } finally {
      setIsExportingPng(false);
    }
  };

  const handleExportSvg = async () => {
    setIsExportingSvg(true);
    try {
      await exportElementsAsSvg(selectedElements, {
        targetElementId: targetElementId === 'all' ? undefined : targetElementId,
        isTransparentBg,
        csvData,
        recordIndex: previewRecordIndex
      });
    } finally {
      setIsExportingSvg(false);
    }
  };

  const handleCopySvgCode = async () => {
    const success = await copyElementsAsSvgCode(selectedElements, {
      targetElementId: targetElementId === 'all' ? undefined : targetElementId,
      isTransparentBg,
      csvData,
      recordIndex: previewRecordIndex
    });
    if (success) {
      setCopiedSvg(true);
      setTimeout(() => setCopiedSvg(false), 2000);
    }
  };

  return (
    <div className="pt-3.5 border-t border-zinc-800 space-y-3 bg-zinc-950/80 -mx-1 px-3 py-3 rounded-lg border border-zinc-800/80 shadow-inner">
      <div className="flex items-center justify-between">
        <label className="text-xs font-bold text-zinc-100 flex items-center gap-1.5">
          <Download className="w-4 h-4 text-emerald-400" />
          <span>Quick Export</span>
        </label>
        <span className="text-[10px] font-mono px-2 py-0.5 rounded bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 font-semibold">
          {selectedElements.length === 1 ? '1 Element' : `${selectedElements.length} Items Selected`}
        </span>
      </div>

      {/* Target Selection Dropdown when multiple elements are selected */}
      {selectedElements.length > 1 && (
        <div>
          <label className="text-[11px] font-semibold text-zinc-400 mb-1 block">Export Scope</label>
          <select
            value={targetElementId}
            onChange={e => setTargetElementId(e.target.value)}
            className="w-full px-2.5 py-1.5 border border-zinc-700 rounded text-xs bg-zinc-900 text-zinc-200 font-medium focus:ring-1 focus:ring-emerald-500 focus:outline-none"
          >
            <option value="all">Entire Selection ({selectedElements.length} combined items)</option>
            <optgroup label="Individual Selected Elements">
              {selectedElements.map((el, idx) => (
                <option key={el.id} value={el.id}>
                  #{idx + 1}: {el.name || el.type.toUpperCase()} ({Math.round(el.width || 100)}×{Math.round(el.height || 100)} px)
                </option>
              ))}
            </optgroup>
          </select>
        </div>
      )}

      {/* Quick Export Configuration */}
      <div className="grid grid-cols-2 gap-2">
        <div>
          <label className="text-[11px] font-medium text-zinc-400 mb-1 block">Background</label>
          <div className="flex bg-zinc-900 p-0.5 rounded border border-zinc-700">
            <button
              type="button"
              onClick={() => setIsTransparentBg(true)}
              className={`flex-1 py-1 text-[11px] font-medium rounded transition-all ${
                isTransparentBg ? 'bg-emerald-600 text-white shadow-sm' : 'text-zinc-400 hover:text-zinc-200'
              }`}
            >
              Transparent
            </button>
            <button
              type="button"
              onClick={() => setIsTransparentBg(false)}
              className={`flex-1 py-1 text-[11px] font-medium rounded transition-all ${
                !isTransparentBg ? 'bg-emerald-600 text-white shadow-sm' : 'text-zinc-400 hover:text-zinc-200'
              }`}
            >
              Solid
            </button>
          </div>
        </div>

        <div>
          <label className="text-[11px] font-medium text-zinc-400 mb-1 block">Resolution / Scale</label>
          <select
            value={scale}
            onChange={e => setScale(Number(e.target.value))}
            className="w-full px-2 py-1 border border-zinc-700 rounded text-xs bg-zinc-900 text-zinc-200 font-medium"
          >
            <option value={1}>1x (Standard)</option>
            <option value={2}>2x (Medium 150 DPI)</option>
            <option value={4}>4x (High-Res 300 DPI)</option>
            <option value={8}>8x (Ultra HD 600 DPI)</option>
          </select>
        </div>
      </div>

      {/* Dimension Badge */}
      <div className="flex items-center justify-between text-[10px] text-zinc-400 font-mono px-2 py-1 bg-zinc-900/80 rounded border border-zinc-800">
        <span>Export Dimensions:</span>
        <span className="text-emerald-400 font-bold">{Math.round(width * scale)} × {Math.round(height * scale)} px</span>
      </div>

      {/* Export Action Buttons */}
      <div className="flex gap-2 pt-1">
        <button
          type="button"
          disabled={isExportingPng}
          onClick={handleExportPng}
          className="flex-1 flex items-center justify-center gap-1.5 py-2 px-3 bg-emerald-600 hover:bg-emerald-500 text-white rounded text-xs font-semibold shadow-sm transition-all disabled:opacity-50 active:scale-95"
          title="Export as Transparent PNG Image"
        >
          {isExportingPng ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ImageIcon className="w-3.5 h-3.5" />}
          <span>PNG ({scale}x)</span>
        </button>

        <button
          type="button"
          disabled={isExportingSvg}
          onClick={handleExportSvg}
          className="flex-1 flex items-center justify-center gap-1.5 py-2 px-3 bg-zinc-800 hover:bg-zinc-700 text-zinc-100 border border-zinc-700 rounded text-xs font-semibold shadow-sm transition-all disabled:opacity-50 active:scale-95"
          title="Export as High-Resolution Vector SVG File"
        >
          {isExportingSvg ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FileCode className="w-3.5 h-3.5 text-amber-400" />}
          <span>SVG Vector</span>
        </button>

        <button
          type="button"
          onClick={handleCopySvgCode}
          className="p-2 bg-zinc-900 hover:bg-zinc-800 text-zinc-300 border border-zinc-700 rounded transition-colors active:scale-95"
          title="Copy SVG Vector Code to Clipboard"
        >
          {copiedSvg ? <Check className="w-4 h-4 text-emerald-400" /> : <Copy className="w-4 h-4" />}
        </button>
      </div>
    </div>
  );
};
